import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "./ui/select";
import { 
  Dialog,
  DialogContent,
} from "./ui/dialog";
import { 
  Check, ChevronLeft, ChevronRight, Plus, Trash2, 
  FileText, DollarSign, Users, Mail, Package, Info,
  Sparkles, Image, Loader2, Lock, Trophy, Calendar,
  BarChart3, Settings, MapPin, Crop, ShoppingBag
} from "lucide-react";
import { Checkbox } from "./ui/checkbox";
import { Switch } from "./ui/switch";
import axios from "axios";
import { toast } from "sonner";
import RichTextEditor from "./RichTextEditor";
import AddressAutocomplete from "./AddressAutocomplete";
import { useFeatureGate, Features } from "../contexts/FeatureGateContext";
import ImageCropper from "./ImageCropper";
import ProgramStepNavigation from "./program-wizard/ProgramStepNavigation";
import "./program-wizard/athleticProgramWizard.css";
import { ProgramLaunchChecklist, ProgramPublishConfirmation } from "./program-wizard/ProgramLaunchReview";
import { ProgramDraftRecovery, ProgramDraftSaveStatus } from "./program-wizard/ProgramDraftRecovery";
import { buildProgramEmailSettingsPayload, getActiveProgramEmailSettings, getProgramReadiness, normalizeProgramType } from "../lib/programCreation";
import { clearProgramDraft, getProgramDraftKey, isProgramDraftMeaningful, loadProgramDraft, normalizeProgramDraft, PROGRAM_WIZARD_VERSION, programDraftsDiverge, saveProgramDraft } from "../lib/programDraft";

const BASE_STEPS = [
  { id: 1, name: "Offering", icon: Info },
  { id: 2, name: "Basics & schedule", icon: Calendar },
  { id: 3, name: "Audience & capacity", icon: Users },
  { id: 4, name: "Pricing", icon: DollarSign },
  { id: 5, name: "Registration setup", icon: FileText },
  { id: 6, name: "Communications", icon: Mail },
  { id: 7, name: "Review & launch", icon: Check },
];

// Simplified League Steps - teams, schedules, standings managed in Program Dashboard after creation
const LEAGUE_STEPS = [
  { id: 1, name: "Program Type", icon: Info },
  { id: 2, name: "League Details", icon: Trophy },
  { id: 3, name: "Registration Type", icon: Users },
  { id: 4, name: "Divisions & Pricing", icon: DollarSign },
  { id: 5, name: "Registration Setup", icon: FileText },
  { id: 6, name: "Payment Plans", icon: DollarSign },
  { id: 7, name: "Email Confirmation", icon: Mail },
  { id: 8, name: "Review & Publish", icon: Check },
];

// Simplified Tournament Steps - bracket, schedule configured in Tournament Dashboard after creation
// Tournament operations use a Marketplace workspace. League creation stays on
// the canonical Program wizard and is unlocked by the Leagues Power-Up.

const PROGRAM_TYPES = [
  { 
    id: "recreational", 
    name: "Community program",
    description: "Classes, academies, training, open gyms, and other participant-enrollment activities",
    icon: "🎮",
    features: ["Participant enrollment", "Flexible groups", "Recurring or date-ranged"],
    tiers: ["free", "plus", "premium", "starter", "pro", "enterprise"] // Available on all tiers
  },
  { 
    id: "competitive", 
    name: "Tryout or evaluation",
    description: "Tryouts, combines, showcases, and evaluations that can feed a future league roster",
    icon: "🏆",
    features: ["Evaluation details", "Participant selection", "Follow-up communication"],
    tiers: ["free", "plus", "premium", "starter", "pro", "enterprise"] // Available on all tiers
  },
  { 
    id: "camp",
    name: "Camp, clinic, or workshop",
    description: "One-time or multi-session offerings organized around capacity and activity groups",
    icon: "⛺",
    features: ["Groups, not teams", "Session-based", "Capacity and check-in"],
    tiers: ["free", "plus", "premium", "starter", "pro", "enterprise"] // Available on all tiers
  },
  // League configuration remains part of the canonical Program domain.
];

// Free tier types
const FREE_TIER_TYPES = ["recreational", "competitive", "camp"];

const sports = ["Soccer", "Basketball", "Baseball", "Swimming", "Tennis", "Football", "Volleyball", "Hockey", "Gymnastics", "Track & Field", "Lacrosse", "Softball", "Wrestling", "Golf", "Cheerleading"];

const ProgramWizard = ({ open, onOpenChange, organizations, organizationsError = "", organizationsLoading = false, onRetryOrganizations, onSuccess, onCancel, editingProgram = null, defaultProgramType = null, mode = "modal" }) => {
  const navigate = useNavigate();
  const [publishConfirmationOpen, setPublishConfirmationOpen] = useState(false);
  const { user, token } = useAuth();
  const { hasFeature } = useFeatureGate();
  const [currentStep, setCurrentStep] = useState(() => {
    if (editingProgram) return 2;
    const normalizedDefault = normalizeProgramType(defaultProgramType);
    return normalizedDefault && (normalizedDefault === "league" || PROGRAM_TYPES.some((type) => type.id === normalizedDefault)) ? 2 : 1;
  });
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState({});
  const [recoveredDraft, setRecoveredDraft] = useState(false);
  const [localDraftSavedAt, setLocalDraftSavedAt] = useState(null);
  const [serverDraftSavedAt, setServerDraftSavedAt] = useState(null);
  const [serverDraftReady, setServerDraftReady] = useState(false);
  const [serverDraftSaving, setServerDraftSaving] = useState(false);
  const [serverDraftLoadError, setServerDraftLoadError] = useState(false);
  const [serverDraftRetryNonce, setServerDraftRetryNonce] = useState(0);
  const [draftConflict, setDraftConflict] = useState(null);
  const [stepIssues, setStepIssues] = useState([]);
  const [validationFocusRequest, setValidationFocusRequest] = useState(0);
  const draftRestoreAttempted = useRef(false);
  const serverDraftRestoreAttempted = useRef(false);
  const errorSummaryRef = useRef(null);
  const pendingValidationFocus = useRef(null);
  const restoredLocalDraft = useRef(null);
  const draftStorageKey = useRef(getProgramDraftKey({
    userId: user?.user_id,
    orgId: user?.org_id,
    programType: normalizeProgramType(defaultProgramType) || "recreational",
  }));
  
  // Image cropper state
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperField, setCropperField] = useState(null);

  // Step 1: Program Details (removed season)
  const [programDetails, setProgramDetails] = useState({
    org_id: user?.org_id || "",
    name: "",
    sport: "",
    program_type: normalizeProgramType(defaultProgramType) || "recreational",
    description: "",
    start_date: "",
    end_date: "",
    registration_open_date: "",
    registration_deadline: "",
    location: "",
    venue_address: "",
    schedule: "",
    max_participants: "",
    logo_url: "",
    splash_image_url: "",
    slug: "",
    // Competitive - Tryouts
    tryouts_enabled: false,
    tryouts_date: "",
    tryouts_location: "",
    tryouts_details: "",
    // Camps - Groups
    groups_enabled: false,
    // Recreational - Friend requests & Auto team building
    friend_requests_enabled: true,
    max_friend_requests: 2,
    friend_request_type: "mutual",
    friend_input_method: "name",
    friend_request_priority: false,
    auto_team_building: true,
    // Registration display options
    show_addons_in_registration: true,
    show_merchandise_in_registration: true,
    // Tournament
    tournament_format: "single_elimination",
    tournament_teams_count: "",
    // League Settings
    league_format: "round_robin",
    league_teams_count: "",
    games_per_team: "10",
    team_roster_min: "8",
    team_roster_max: "15",
    registration_type: "individual", // individual, team, hybrid
    playoffs_enabled: true,
    playoff_teams: "4",
    playoff_format: "single_elimination",
  });

  // League Settings State
  const [leagueSettings, setLeagueSettings] = useState({
    // Schedule Settings
    game_days: ["Saturday"],
    time_slots: ["09:00", "11:00", "13:00", "15:00"],
    game_duration: "60",
    venues: [""],
    blackout_dates: [],
    // Standings Settings
    points_win: "3",
    points_loss: "0",
    points_tie: "1",
    tiebreakers: ["head_to_head", "goal_differential", "goals_for"],
    track_stats: true,
    stats_to_track: ["goals", "assists"],
    display_standings_publicly: true,
    // Rules
    rules_document: "",
  });

  // Step 2: Divisions - simplified with age range (now using birth date ranges)
  const [divisions, setDivisions] = useState([
    { 
      name: "", 
      birth_date_start: "", // Oldest (earliest birth date)
      birth_date_end: "",   // Youngest (latest birth date)
      age_range: "",        // Legacy/display field
      gender: "coed", 
      max_players: "", 
      price: "" 
    }
  ]);

  // Step 3: Payment Plans - Full Pay is default and non-deletable
  const [paymentPlans, setPaymentPlans] = useState([
    { name: "Full Payment", num_payments: 1, deposit: "", is_default: true, is_required: true }
  ]);
  const [earlyBirdDiscount, setEarlyBirdDiscount] = useState({
    enabled: false,
    discount_percent: "",
    deadline: ""
  });

  // Step 4: Documents - with content field
  const [documents, setDocuments] = useState([
    { name: "Liability Waiver", type: "waiver", required: true, description: "", content: "" }
  ]);

  // Step 5: Add-ons - with photo, size, and color options
  const [addons, setAddons] = useState([]);

  // Size and color options for add-ons
  const sizeOptions = ["XS", "S", "M", "L", "XL", "XXL", "Youth S", "Youth M", "Youth L"];
  const colorOptions = ["Black", "White", "Navy", "Red", "Royal Blue", "Green", "Gray", "Orange", "Purple", "Yellow"];

  // Step 6: Email Confirmation
  const [emailSettings, setEmailSettings] = useState({
    confirmation_subject: "Registration Confirmed - {program_name}",
    confirmation_body: `Dear {parent_name},

Thank you for registering {player_name} for {program_name}!

Program Details:
- Start Date: {start_date}
- Location: {location}

We look forward to seeing you!

Best regards,
{organization_name}`,
    include_calendar_invite: false,
    send_reminder_before_start: false,
    reminder_days: "7"
  });

  // Determine which steps to show based on program type
  const isLeague = programDetails.program_type === "league";
  const STEPS = isLeague ? LEAGUE_STEPS : BASE_STEPS;
  const totalSteps = STEPS.length;
  const leavePage = () => onCancel ? onCancel() : navigate(-1);

  // Helper to get step content ID based on program type
  const getStepContentId = (stepNumber) => {
    if (!isLeague) {
      return stepNumber; // Base steps: 1-7
    }
    // League steps have different mappings
    return stepNumber;
  };

  // Load existing program data when editing
  useEffect(() => {
    if (editingProgram && (open || mode === "page")) {
      setProgramDetails({
        org_id: editingProgram.org_id || user?.org_id || "",
        name: editingProgram.name || "",
        sport: editingProgram.sport || "",
        description: editingProgram.description || "",
        start_date: editingProgram.start_date?.split('T')[0] || "",
        end_date: editingProgram.end_date?.split('T')[0] || "",
        registration_open_date: editingProgram.registration_open_date?.split('T')[0] || "",
        registration_deadline: editingProgram.registration_deadline?.split('T')[0] || "",
        location: editingProgram.location || "",
        venue_address: editingProgram.venue_address || "",
        schedule: editingProgram.schedule || "",
        max_participants: editingProgram.max_participants?.toString() || "",
        logo_url: editingProgram.logo_url || "",
        splash_image_url: editingProgram.splash_image_url || "",
        slug: editingProgram.slug || "",
        program_type: normalizeProgramType(editingProgram.program_type) || "recreational",
        tryouts_enabled: editingProgram.tryouts_enabled || false,
        tryouts_date: editingProgram.tryouts_date?.split('T')[0] || "",
        tryouts_location: editingProgram.tryouts_location || "",
        tryouts_details: editingProgram.tryouts_details || "",
        groups_enabled: editingProgram.groups_enabled || false,
        friend_requests_enabled: editingProgram.friend_requests_enabled ?? true,
        max_friend_requests: editingProgram.max_friend_requests ?? 2,
        friend_request_type: editingProgram.friend_request_type || "mutual",
        friend_input_method: editingProgram.friend_input_method || "name",
        friend_request_priority: editingProgram.friend_request_priority || false,
        auto_team_building: editingProgram.auto_team_building ?? true,
        show_addons_in_registration: editingProgram.show_addons_in_registration ?? true,
        show_merchandise_in_registration: editingProgram.show_merchandise_in_registration ?? true,
        tournament_format: editingProgram.tournament_format || "single_elimination",
        tournament_teams_count: editingProgram.tournament_teams_count?.toString() || "",
      });
      
      if (editingProgram.divisions?.length > 0) {
        setDivisions(editingProgram.divisions);
      }
      if (editingProgram.payment_plans?.length > 0) {
        setPaymentPlans(editingProgram.payment_plans);
      }
      if (editingProgram.required_documents?.length > 0) {
        setDocuments(editingProgram.required_documents);
        // Also populate waivers state for league mode
        setWaivers(editingProgram.required_documents.map(d => ({
          title: d.name || d.title || "",
          content: d.content || "",
          is_required: d.required ?? d.is_required ?? true
        })));
      }
      if (editingProgram.addons?.length > 0) {
        setAddons(editingProgram.addons);
      }
      if (editingProgram.email_settings) {
        setEmailSettings(editingProgram.email_settings);
      }
      // Load early bird discount settings
      if (editingProgram.early_bird_discount) {
        setEarlyBirdDiscount({
          enabled: true,
          discount_percent: editingProgram.early_bird_discount.discount_percent?.toString() || "",
          deadline: editingProgram.early_bird_discount.deadline?.split('T')[0] || ""
        });
      }
    }
  }, [editingProgram, open, mode, user?.org_id]);

  // Age range quick select options
  const currentYear = new Date().getFullYear();
  
  // Generate birth year options (for players aged 3-18)
  const birthYearOptions = [];
  for (let i = 3; i <= 18; i++) {
    const year = currentYear - i;
    birthYearOptions.push({
      label: `${year} (${i} years old)`,
      year: year,
      start: `${year}-01-01`,
      end: `${year}-12-31`
    });
  }
  
  // Generate school year options (Aug 1 - Jul 31)
  const schoolYearOptions = [];
  for (let i = 3; i <= 18; i++) {
    const startYear = currentYear - i - 1;
    const endYear = currentYear - i;
    schoolYearOptions.push({
      label: `School Year ${startYear}-${endYear.toString().slice(2)} (≈${i} yrs)`,
      start: `${startYear}-08-01`,
      end: `${endYear}-07-31`
    });
  }

  // Helper to format date range for display
  const formatDateRange = (start, end) => {
    if (!start || !end) return "Not set";
    const startDate = new Date(start);
    const endDate = new Date(end);
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();
    
    if (startYear === endYear) {
      return `Born in ${startYear}`;
    }
    return `Born ${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  // Helper to calculate age range from birth dates
  const calculateAgeRange = (startDate, endDate) => {
    if (!startDate || !endDate) return "";
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const oldestAge = Math.floor((today - start) / (365.25 * 24 * 60 * 60 * 1000));
    const youngestAge = Math.floor((today - end) / (365.25 * 24 * 60 * 60 * 1000));
    
    return `${youngestAge}-${oldestAge}`;
  };

  const addDivision = () => {
    setDivisions([...divisions, { 
      name: "", 
      birth_date_start: "", 
      birth_date_end: "", 
      age_range: "", 
      gender: "coed", 
      max_players: "", 
      price: "" 
    }]);
  };

  const removeDivision = (index) => {
    if (divisions.length > 1) {
      setDivisions(divisions.filter((_, i) => i !== index));
    }
  };

  const updateDivision = (index, field, value) => {
    const updated = [...divisions];
    updated[index][field] = value;
    setDivisions(updated);
    setStepIssues((issues) => issues.filter((issue) => issue.field !== `divisions.${index}.${field}`));
  };

  const addPaymentPlan = () => {
    setPaymentPlans([...paymentPlans, { name: "", num_payments: 2, deposit: "", is_default: false, is_required: false }]);
  };

  const removePaymentPlan = (index) => {
    // Can't remove the required Full Payment plan
    if (paymentPlans[index].is_required) {
      toast.error("Full Payment option cannot be removed");
      return;
    }
    setPaymentPlans(paymentPlans.filter((_, i) => i !== index));
  };

  const updatePaymentPlan = (index, field, value) => {
    const updated = [...paymentPlans];
    if (field === "is_default" && value) {
      updated.forEach((p, i) => p.is_default = i === index);
    } else {
      updated[index][field] = value;
    }
    setPaymentPlans(updated);
    setStepIssues((issues) => issues.filter((issue) => issue.field !== `paymentPlans.${index}.${field}`));
  };

  const addDocument = () => {
    setDocuments([...documents, { name: "", type: "form", required: false, description: "", content: "" }]);
  };

  const removeDocument = (index) => {
    setDocuments(documents.filter((_, i) => i !== index));
  };

  const updateDocument = (index, field, value) => {
    const updated = [...documents];
    updated[index][field] = value;
    setDocuments(updated);
  };

  // Waivers state and functions (separate from documents for specific waiver management)
  const [waivers, setWaivers] = useState([]);
  const [confirmationEmail, setConfirmationEmail] = useState({
    subject: "Registration Confirmed - {program_name}",
    body: ""
  });

  const hydrateProgramDraft = useCallback((draft, savedStep) => {
    const data = normalizeProgramDraft({ ...draft, currentStep: savedStep });
    if (data.programDetails) setProgramDetails(data.programDetails);
    if (data.leagueSettings) setLeagueSettings(data.leagueSettings);
    if (data.divisions) setDivisions(data.divisions);
    if (data.paymentPlans) setPaymentPlans(data.paymentPlans);
    if (data.earlyBirdDiscount) setEarlyBirdDiscount(data.earlyBirdDiscount);
    if (data.documents) setDocuments(data.documents);
    if (data.addons) setAddons(data.addons);
    if (data.emailSettings) setEmailSettings(data.emailSettings);
    if (data.waivers) setWaivers(data.waivers);
    if (data.confirmationEmail) setConfirmationEmail(data.confirmationEmail);
    if (data.currentStep) setCurrentStep(data.currentStep);
    setRecoveredDraft(true);
  }, []);

  useEffect(() => {
    if (editingProgram || (mode === "modal" && !open) || draftRestoreAttempted.current) return;
    draftRestoreAttempted.current = true;
    const savedDraft = loadProgramDraft(window.localStorage, draftStorageKey.current);
    if (!savedDraft) return;
    restoredLocalDraft.current = savedDraft;
    hydrateProgramDraft(savedDraft, savedDraft.currentStep);
    setLocalDraftSavedAt(savedDraft.savedAt);
  }, [editingProgram, hydrateProgramDraft, mode, open]);

  useEffect(() => {
    if (!token || editingProgram || (mode === "modal" && !open) || serverDraftRestoreAttempted.current) return;
    serverDraftRestoreAttempted.current = true;
    setServerDraftLoadError(false);
    const programType = normalizeProgramType(defaultProgramType) || programDetails.program_type;
    axios.get(`${API}/programs/drafts/current`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { program_type: programType },
    }).then((response) => {
      const draft = response.data;
      const data = draft?.data;
      if (!data) {
        setServerDraftReady(true);
        return;
      }
      const serverData = { ...data, currentStep: draft.current_step };
      const localData = restoredLocalDraft.current;
      if (localData && programDraftsDiverge(localData, serverData)) {
        setDraftConflict({
          local: localData,
          server: data,
          localSavedAt: localData.savedAt,
          serverSavedAt: draft.updated_at,
          serverStep: draft.current_step,
        });
        return;
      }
      hydrateProgramDraft(data, draft.current_step);
      setServerDraftSavedAt(draft.updated_at);
      setServerDraftReady(true);
    }).catch(() => {
      // Keep server autosave fail-closed until we know whether a newer cloud copy exists.
      setServerDraftLoadError(true);
      setServerDraftReady(false);
    });
  }, [defaultProgramType, editingProgram, hydrateProgramDraft, mode, open, programDetails.program_type, serverDraftRetryNonce, token]);

  useEffect(() => {
    if (editingProgram || (mode === "modal" && !open) || !draftRestoreAttempted.current) return undefined;
    const autosaveTimer = window.setTimeout(() => {
      if (!isProgramDraftMeaningful(programDetails)) return;
      const savedAt = saveProgramDraft(window.localStorage, draftStorageKey.current, {
        programWizardVersion: PROGRAM_WIZARD_VERSION,
        currentStep,
        programDetails,
        leagueSettings,
        divisions,
        paymentPlans,
        earlyBirdDiscount,
        documents,
        addons,
        emailSettings,
        waivers,
        confirmationEmail,
      });
      if (savedAt) setLocalDraftSavedAt(savedAt);
    }, 600);
    return () => window.clearTimeout(autosaveTimer);
  }, [editingProgram, mode, open, currentStep, programDetails, leagueSettings, divisions, paymentPlans, earlyBirdDiscount, documents, addons, emailSettings, waivers, confirmationEmail]);

  useEffect(() => {
    if (!serverDraftReady || editingProgram || (mode === "modal" && !open) || !isProgramDraftMeaningful(programDetails)) return undefined;
    const autosaveTimer = window.setTimeout(() => {
      setServerDraftSaving(true);
      axios.put(`${API}/programs/drafts/current`, {
        program_type: normalizeProgramType(programDetails.program_type),
        current_step: currentStep,
        data: { programWizardVersion: PROGRAM_WIZARD_VERSION, programDetails, leagueSettings, divisions, paymentPlans, earlyBirdDiscount, documents, addons, emailSettings, waivers, confirmationEmail },
      }, { headers: { Authorization: `Bearer ${token}` } }).then((response) => {
        setServerDraftSavedAt(response.data?.updated_at || new Date().toISOString());
      }).catch(() => {
        // Local autosave remains the fallback; explicit Save and exit surfaces failures.
      }).finally(() => setServerDraftSaving(false));
    }, 1200);
    return () => window.clearTimeout(autosaveTimer);
  }, [addons, confirmationEmail, currentStep, divisions, documents, earlyBirdDiscount, editingProgram, emailSettings, leagueSettings, mode, open, paymentPlans, programDetails, serverDraftReady, token, waivers]);

  const addWaiver = () => {
    setWaivers([...waivers, { title: "", content: "", is_required: true }]);
  };

  const removeWaiver = (index) => {
    setWaivers(waivers.filter((_, i) => i !== index));
  };

  const updateWaiver = (index, field, value) => {
    const updated = [...waivers];
    updated[index][field] = value;
    setWaivers(updated);
  };

  const generateWaiverContent = async (index) => {
    setAiLoading({ ...aiLoading, [`waiver_${index}`]: true });
    try {
      const waiver = waivers[index];
      const content = `PARTICIPANT WAIVER AND RELEASE OF LIABILITY - ${waiver.title || 'General Waiver'}

I, the undersigned parent/guardian, hereby acknowledge and agree to the following:

1. ASSUMPTION OF RISK: I understand that participation in ${programDetails.name || 'this program'} involves inherent risks.

2. RELEASE OF LIABILITY: I hereby release and discharge the organization from any claims arising from participation.

3. MEDICAL AUTHORIZATION: In case of emergency, I authorize program staff to seek medical treatment.

4. PHOTO/VIDEO RELEASE: I grant permission for photographs and videos to be used for promotional purposes.

By signing, I acknowledge that I have read and agree to these terms.

Parent/Guardian Signature: _______________________
Date: _______________________`;
      
      updateWaiver(index, "content", content);
      toast.success("Waiver content generated!");
    } catch (error) {
      toast.error("Failed to generate content");
    } finally {
      setAiLoading({ ...aiLoading, [`waiver_${index}`]: false });
    }
  };

  const generateEmailContent = async () => {
    setAiLoading({ ...aiLoading, email: true });
    try {
      const emailBody = `Dear {parent_name},

Thank you for registering {player_name} for ${programDetails.name || '{program_name}'}!

Program Details:
- Start Date: {start_date}
- Location: ${programDetails.location || '{location}'}

We look forward to seeing you!

Best regards,
{organization_name}`;
      
      setConfirmationEmail(prev => ({ ...prev, body: emailBody }));
      toast.success("Email content generated!");
    } catch (error) {
      toast.error("Failed to generate email content");
    } finally {
      setAiLoading({ ...aiLoading, email: false });
    }
  };

  const setDefaultPaymentPlan = (index) => {
    const updated = paymentPlans.map((plan, i) => ({
      ...plan,
      is_default: i === index
    }));
    setPaymentPlans(updated);
  };

  const addAddon = () => {
    setAddons([...addons, { 
      name: "", 
      description: "", 
      price: "", 
      max_quantity: "1", 
      required: false, 
      photo_url: "",
      has_sizes: false,
      available_sizes: [],
      has_colors: false,
      available_colors: []
    }]);
  };

  const removeAddon = (index) => {
    setAddons(addons.filter((_, i) => i !== index));
  };

  const updateAddon = (index, field, value) => {
    const updated = [...addons];
    updated[index][field] = value;
    setAddons(updated);
  };

  // AI Assistant functions
  const generateDocumentWithAI = async (index, docType) => {
    setAiLoading({ ...aiLoading, [`doc_${index}`]: true });
    
    // Simulated AI generation (in real app, this would call an AI API)
    const docTemplates = {
      waiver: `PARTICIPANT WAIVER AND RELEASE OF LIABILITY

I, the undersigned parent/guardian of the participant, hereby acknowledge and agree to the following:

1. ASSUMPTION OF RISK: I understand that participation in ${programDetails.name || 'this program'} involves inherent risks, including but not limited to physical injury, property damage, and other dangers.

2. RELEASE OF LIABILITY: I hereby release and discharge ${programDetails.name || 'the organization'}, its officers, directors, employees, volunteers, and agents from any and all claims, damages, or causes of action arising from participation in the program.

3. MEDICAL AUTHORIZATION: In case of emergency, I authorize program staff to seek medical treatment for my child if I cannot be reached.

4. PHOTO/VIDEO RELEASE: I grant permission for photographs and videos of my child to be used for promotional purposes.

5. RULES AND CONDUCT: I agree that my child will follow all program rules and display good sportsmanship at all times.

By signing below, I acknowledge that I have read, understood, and agree to the terms of this waiver.

Parent/Guardian Signature: _______________________
Date: _______________________
Participant Name: _______________________`,
      
      form: `PARTICIPANT INFORMATION FORM

Program: ${programDetails.name || '[Program Name]'}

PARTICIPANT INFORMATION:
Full Name: _______________________
Date of Birth: _______________________
Grade: _______________________
School: _______________________

PARENT/GUARDIAN INFORMATION:
Name: _______________________
Relationship: _______________________
Phone: _______________________
Email: _______________________

EMERGENCY CONTACT:
Name: _______________________
Phone: _______________________
Relationship: _______________________

MEDICAL INFORMATION:
Allergies: _______________________
Medical Conditions: _______________________
Medications: _______________________
Physician Name: _______________________
Physician Phone: _______________________

ADDITIONAL NOTES:
_______________________`,

      agreement: `PROGRAM PARTICIPATION AGREEMENT

This agreement is entered into between the parent/guardian and ${programDetails.name || 'the organization'}.

PROGRAM COMMITMENT:
- I commit to ensuring my child attends all scheduled practices and games
- I will notify coaches in advance of any absences
- I will arrange for timely drop-off and pick-up

PARENT CONDUCT:
- I will demonstrate positive sportsmanship at all events
- I will respect coaches, officials, and other participants
- I will refrain from coaching from the sidelines

FINANCIAL COMMITMENT:
- I understand payment is due as specified in the registration
- I understand fees are non-refundable unless otherwise stated

By signing, I agree to uphold these commitments throughout the program.

Signature: _______________________
Date: _______________________`
    };

    await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate AI delay
    
    const template = docTemplates[docType] || docTemplates.form;
    updateDocument(index, 'content', template);
    
    setAiLoading({ ...aiLoading, [`doc_${index}`]: false });
    toast.success("Document generated with AI!");
  };

  const generateEmailWithAI = async () => {
    setAiLoading({ ...aiLoading, email: true });
    
    await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate AI delay
    
    const generatedEmail = `Dear {parent_name},

🎉 Great news! {player_name} is officially registered for ${programDetails.name || '{program_name}'}!

📋 REGISTRATION DETAILS
━━━━━━━━━━━━━━━━━━━━━
Player: {player_name}
Program: {program_name}
Start Date: {start_date}
Location: {location}

📍 WHAT TO BRING
• Comfortable athletic clothing
• Water bottle
• Positive attitude!

📞 QUESTIONS?
Feel free to reach out to us anytime. We're here to help make this a fantastic experience for your family.

We can't wait to see {player_name} on the field!

Warm regards,
The {organization_name} Team

---
This is an automated confirmation. Please do not reply directly to this email.`;

    setEmailSettings({
      ...emailSettings,
      confirmation_subject: `🎉 You're In! Registration Confirmed for ${programDetails.name || '{program_name}'}`,
      confirmation_body: generatedEmail
    });
    
    setAiLoading({ ...aiLoading, email: false });
    toast.success("Email generated with AI!");
  };

  const handlePhotoUpload = async (index, e) => {
    const file = e.target.files?.[0];
    const target = addons[index];
    if (!file || !target) return;
    if (!["image/jpeg", "image/png", "image/gif", "image/webp"].includes(file.type) || file.size > 10 * 1024 * 1024) {
      toast.error("Choose a JPG, PNG, GIF, or WebP image up to 10MB.");
      return;
    }
    // Keep the old photo until upload succeeds; never persist a browser-local blob URL.
    try {
      const data = new FormData();
      data.append('file', file);
      data.append('image_type', 'product');
      const response = await axios.post(`${API}/upload/direct`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const url = new URL(response.data.url);
      if (!['https:', 'http:'].includes(url.protocol)) throw new Error('Invalid upload URL');
      // Match the original item, not an index that may now belong to another item.
      setAddons(previous => previous.map(item => item === target ? { ...item, photo_url: url.href } : item));
      toast.success("Photo uploaded. Save the program to publish it.");
    } catch (error) {
      toast.error("Photo upload failed. Please try again.");
    }
  };

  const handleProgramImageUpload = async (field, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPG, PNG, GIF, or WebP.");
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      return;
    }

    // For splash images, open the cropper first
    if (field === 'splash_image_url') {
      const previewUrl = URL.createObjectURL(file);
      setCropperImage(previewUrl);
      setCropperField(field);
      setCropperOpen(true);
      // Clear the input so the same file can be selected again
      e.target.value = '';
      return;
    }

    // For logos, upload directly without cropping
    const previewUrl = URL.createObjectURL(file);
    setProgramDetails({ ...programDetails, [field]: previewUrl });

    try {
      // Use direct upload endpoint (goes through backend, no CORS issues)
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', field === 'logo_url' ? 'program-logo' : 'program-splash');
      if (programDetails.program_id) {
        formData.append('entity_id', programDetails.program_id);
      }

      const uploadRes = await axios.post(
        `${API}/upload/direct`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      // Update with permanent URL
      setProgramDetails({ ...programDetails, [field]: uploadRes.data.url });
      toast.success(field === 'logo_url' ? "Logo uploaded!" : "Splash image uploaded!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Upload failed. Please try again.");
      // Revert to empty on error
      setProgramDetails({ ...programDetails, [field]: '' });
    }
  };

  // Handle cropped image upload
  const handleCroppedImageUpload = async (croppedFile) => {
    setCropperOpen(false);
    
    // Show preview immediately
    const previewUrl = URL.createObjectURL(croppedFile);
    setProgramDetails({ ...programDetails, [cropperField]: previewUrl });

    try {
      const formData = new FormData();
      formData.append('file', croppedFile);
      formData.append('image_type', 'program-splash');
      if (programDetails.program_id) {
        formData.append('entity_id', programDetails.program_id);
      }

      const uploadRes = await axios.post(
        `${API}/upload/direct`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      setProgramDetails({ ...programDetails, [cropperField]: uploadRes.data.url });
      toast.success("Splash image cropped and uploaded!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Upload failed. Please try again.");
      setProgramDetails({ ...programDetails, [cropperField]: '' });
    }
    
    // Clean up
    setCropperImage(null);
    setCropperField(null);
  };

  const getReadiness = () => {
    const activeEmailSettings = getActiveProgramEmailSettings({ isLeague, emailSettings, confirmationEmail });
    const readiness = getProgramReadiness({ programDetails, divisions, paymentPlans, emailSettings: activeEmailSettings });
    if (!isLeague) return readiness;
    return {
      ...readiness,
      issues: readiness.issues.map((issue) => ({
        ...issue,
        step: issue.step === 3 ? 4 : issue.step === 4 ? 6 : issue.step === 6 ? 7 : issue.step,
      })),
    };
  };

  const goToStep = (step) => {
    setStepIssues([]);
    setCurrentStep(step);
  };

  const fieldIssue = (field) => stepIssues.find((issue) => issue.field === field);
  const fieldErrorId = (field) => `program-field-error-${field.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  const fieldErrorProps = (field) => ({
    "data-program-field": field,
    "aria-invalid": Boolean(fieldIssue(field)) || undefined,
    "aria-describedby": fieldIssue(field) ? fieldErrorId(field) : undefined,
  });
  const renderFieldError = (field) => {
    const issue = fieldIssue(field);
    return issue ? <p id={fieldErrorId(field)} className="mt-1 text-xs font-medium text-red-700">{issue.label}</p> : null;
  };
  const clearFieldIssue = (field) => setStepIssues((issues) => issues.filter((issue) => issue.field !== field));

  useEffect(() => {
    if (!validationFocusRequest) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const field = pendingValidationFocus.current;
      const control = field ? document.querySelector(`[data-program-field="${field}"]`) : null;
      (control || errorSummaryRef.current)?.focus();
      pendingValidationFocus.current = null;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [currentStep, validationFocusRequest]);

  const nextStep = () => {
    setStepIssues([]);
    setCurrentStep(Math.min(currentStep + 1, totalSteps));
  };

  const prevStep = () => {
    setCurrentStep(Math.max(currentStep - 1, 1));
  };

  const handleSubmit = async (publish = false) => {
    const readiness = getReadiness();
    if (!readiness.ready) {
      const firstIssue = readiness.issues[0];
      pendingValidationFocus.current = firstIssue.field || null;
      setCurrentStep(firstIssue.step);
      setStepIssues(readiness.issues.filter((issue) => issue.step === firstIssue.step));
      setValidationFocusRequest((request) => request + 1);
      return;
    }

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Calculate total max participants from divisions
      const totalMaxParticipants = divisions.reduce((sum, d) => sum + (parseInt(d.max_players) || 0), 0);
      
      // Get base price from first division
      const basePrice = parseFloat(divisions[0]?.price) || 0;
      
      // Calculate overall age range from divisions
      const allAges = divisions.flatMap(d => {
        const ageRange = calculateAgeRange(d.birth_date_start, d.birth_date_end);
        return ageRange ? ageRange.split('-').map(Number) : [5, 10];
      });
      const overallAgeMin = parseInt(Math.min(...allAges)) || 5;
      const overallAgeMax = parseInt(Math.max(...allAges)) || 18;

      const activeEmailSettings = getActiveProgramEmailSettings({ isLeague, emailSettings, confirmationEmail });
      const payload = {
        org_id: programDetails.org_id,
        name: programDetails.name,
        sport: programDetails.sport,
        program_type: normalizeProgramType(programDetails.program_type) || "recreational",
        status: publish ? "active" : (editingProgram?.status === "active" ? "active" : "draft"),
        description: programDetails.description || "",
        season: "Year-Round",
        age_min: overallAgeMin,
        age_max: overallAgeMax,
        start_date: programDetails.start_date,
        end_date: programDetails.end_date,
        registration_open_date: programDetails.registration_open_date || null,
        registration_deadline: programDetails.registration_deadline,
        max_participants: parseInt(totalMaxParticipants) || parseInt(programDetails.max_participants) || 100,
        price: basePrice,
        location: programDetails.location || "",
        venue_address: programDetails.venue_address || "",
        schedule: programDetails.schedule || "",
        slug: programDetails.slug || "",
        logo_url: programDetails.logo_url || "",
        splash_image_url: programDetails.splash_image_url || "",
        tryouts_enabled: Boolean(programDetails.tryouts_enabled),
        tryouts_date: programDetails.tryouts_date || null,
        tryouts_location: programDetails.tryouts_location || null,
        tryouts_details: programDetails.tryouts_details || null,
        groups_enabled: Boolean(programDetails.groups_enabled),
        friend_requests_enabled: Boolean(programDetails.friend_requests_enabled),
        auto_team_building: Boolean(programDetails.auto_team_building),
        show_addons_in_registration: programDetails.show_addons_in_registration !== false,
        show_merchandise_in_registration: programDetails.show_merchandise_in_registration !== false,
        tournament_format: programDetails.tournament_format || null,
        tournament_teams_count: programDetails.tournament_teams_count ? parseInt(programDetails.tournament_teams_count) : null,
        // League settings
        league_format: programDetails.league_format || null,
        league_teams_count: programDetails.league_teams_count ? parseInt(programDetails.league_teams_count) : null,
        games_per_team: programDetails.games_per_team ? parseInt(programDetails.games_per_team) : null,
        team_roster_min: programDetails.team_roster_min ? parseInt(programDetails.team_roster_min) : null,
        team_roster_max: programDetails.team_roster_max ? parseInt(programDetails.team_roster_max) : null,
        registration_type: programDetails.registration_type || null,
        playoffs_enabled: Boolean(programDetails.playoffs_enabled),
        playoff_teams: programDetails.playoff_teams ? parseInt(programDetails.playoff_teams) : null,
        playoff_format: programDetails.playoff_format || null,
        league_settings: programDetails.program_type === 'league' ? {
          ...leagueSettings,
          game_duration: parseInt(leagueSettings.game_duration) || 60,
          points_win: parseInt(leagueSettings.points_win) || 3,
          points_loss: parseInt(leagueSettings.points_loss) || 0,
          points_tie: parseInt(leagueSettings.points_tie) || 1,
        } : null,
        pre_registration_enabled: Boolean(programDetails.pre_registration_enabled),
        pre_registration_deadline: programDetails.pre_registration_deadline || null,
        payment_due_date: programDetails.payment_due_date || null,
        auto_send_payment_reminder: programDetails.auto_send_payment_reminder !== false,
        divisions: divisions.map(d => {
          // Calculate age range from birth dates for backward compatibility
          const ageRange = calculateAgeRange(d.birth_date_start, d.birth_date_end);
          const ages = ageRange ? ageRange.split('-').map(Number) : [5, 10];
          return {
            ...d,
            age_range: ageRange || d.age_range,
            birth_date_start: d.birth_date_start,
            birth_date_end: d.birth_date_end,
            age_min: parseInt(ages[0]) || 5,
            age_max: parseInt(ages[1]) || 10,
            max_players: parseInt(d.max_players) || 30,
            price: parseFloat(d.price) || 0
          };
        }),
        payment_plans: paymentPlans.map(p => ({
          ...p,
          num_payments: parseInt(p.num_payments) || 1,
          deposit: parseFloat(p.deposit) || 0
        })),
        early_bird_discount: earlyBirdDiscount.enabled ? {
          discount_percent: parseFloat(earlyBirdDiscount.discount_percent),
          deadline: earlyBirdDiscount.deadline
        } : null,
        required_documents: isLeague ? waivers.map(w => ({ name: w.title, type: "waiver", required: w.is_required, description: "", content: w.content })) : documents,
        addons: addons.map(a => ({
          ...a,
          price: parseFloat(a.price) || 0,
          max_quantity: parseInt(a.max_quantity) || 1
        })),
        email_settings: buildProgramEmailSettingsPayload(activeEmailSettings)
      };

      if (editingProgram) {
        await axios.put(`${API}/programs/${editingProgram.program_id}`, payload, { headers });
        toast.success(publish ? "Program updated and published!" : "Program draft saved!");
        
        // Close dialog and call success callback
        onOpenChange(false);
        clearProgramDraft(window.localStorage, draftStorageKey.current);
        await axios.delete(`${API}/programs/drafts/current`, { headers, params: { program_type: normalizeProgramType(programDetails.program_type) } }).catch(() => {});
        resetForm();
        setTimeout(() => {
          onSuccess?.(editingProgram.program_id);
        }, 100);
      } else {
        const response = await axios.post(`${API}/programs`, payload, { headers });
        toast.success(publish ? "Program published successfully!" : "Program saved as a draft!");
        const newProgramId = response.data.program_id;
        
        // Close dialog and navigate to new program
        onOpenChange(false);
        clearProgramDraft(window.localStorage, draftStorageKey.current);
        await axios.delete(`${API}/programs/drafts/current`, { headers, params: { program_type: normalizeProgramType(programDetails.program_type) } }).catch(() => {});
        resetForm();
        setTimeout(() => {
          onSuccess?.(newProgramId);
        }, 100);
      }
    } catch (error) {
      console.error("Program save error:", error);
      // Extract error message safely
      let errorMessage = "Failed to save program";
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors come as an array
          errorMessage = error.response.data.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    const normalizedDefault = normalizeProgramType(defaultProgramType);
    setCurrentStep(normalizedDefault && PROGRAM_TYPES.some((type) => type.id === normalizedDefault) ? 2 : 1);
    setProgramDetails({
      org_id: user?.org_id || "",
      name: "",
      sport: "",
      program_type: normalizedDefault || "recreational",
      description: "",
      start_date: "",
      end_date: "",
      registration_open_date: "",
      registration_deadline: "",
      location: "",
      venue_address: "",
      schedule: "",
      max_participants: "",
      logo_url: "",
      splash_image_url: "",
      slug: "",
      tryouts_enabled: false,
      tryouts_date: "",
      tryouts_location: "",
      tryouts_details: "",
      groups_enabled: false,
      friend_requests_enabled: true,
      auto_team_building: true,
      show_addons_in_registration: true,
      show_merchandise_in_registration: true,
      tournament_format: "single_elimination",
      tournament_teams_count: "",
      // League
      league_format: "round_robin",
      league_teams_count: "",
      games_per_team: "10",
      team_roster_min: "8",
      team_roster_max: "15",
      registration_type: "individual",
      playoffs_enabled: true,
      playoff_teams: "4",
      playoff_format: "single_elimination",
    });
    setLeagueSettings({
      game_days: ["Saturday"],
      time_slots: ["09:00", "11:00", "13:00", "15:00"],
      game_duration: "60",
      venues: [""],
      blackout_dates: [],
      points_win: "3",
      points_loss: "0",
      points_tie: "1",
      tiebreakers: ["head_to_head", "goal_differential", "goals_for"],
      track_stats: true,
      stats_to_track: ["goals", "assists"],
      display_standings_publicly: true,
      rules_document: "",
    });
    setDivisions([{ 
      name: "", 
      birth_date_start: "", 
      birth_date_end: "", 
      age_range: "", 
      gender: "coed", 
      max_players: "", 
      price: "" 
    }]);
    setPaymentPlans([{ name: "Full Payment", num_payments: 1, deposit: "", is_default: true, is_required: true }]);
    setEarlyBirdDiscount({ enabled: false, discount_percent: "", deadline: "" });
    setDocuments([{ name: "Liability Waiver", type: "waiver", required: true, description: "", content: "" }]);
    setAddons([]);
    setEmailSettings({
      confirmation_subject: "Registration Confirmed - {program_name}",
      confirmation_body: `Dear {parent_name},

Thank you for registering {player_name} for {program_name}!

Program Details:
- Start Date: {start_date}
- Location: {location}

We look forward to seeing you!

Best regards,
{organization_name}`,
      include_calendar_invite: false,
      send_reminder_before_start: false,
      reminder_days: "7"
    });
    setRecoveredDraft(false);
    setLocalDraftSavedAt(null);
    setServerDraftSavedAt(null);
    setDraftConflict(null);
    setServerDraftLoadError(false);
  };

  const discardLocalDraft = () => {
    clearProgramDraft(window.localStorage, draftStorageKey.current);
    axios.delete(`${API}/programs/drafts/current`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { program_type: normalizeProgramType(programDetails.program_type) },
    }).catch(() => {});
    resetForm();
    toast.success("Recovered setup discarded");
  };

  const useLocalDraft = () => {
    setDraftConflict(null);
    setServerDraftReady(true);
    setRecoveredDraft(true);
    toast.success("Using this device’s saved setup");
  };

  const useServerDraft = () => {
    if (!draftConflict) return;
    hydrateProgramDraft(draftConflict.server, draftConflict.serverStep);
    setServerDraftSavedAt(draftConflict.serverSavedAt);
    setDraftConflict(null);
    setServerDraftReady(true);
    toast.success("Using the setup saved to Sporthouse");
  };

  const retryServerDraft = () => {
    serverDraftRestoreAttempted.current = false;
    setServerDraftRetryNonce((value) => value + 1);
  };

  const saveAndExit = async () => {
    setServerDraftSaving(true);
    try {
      const response = await axios.put(`${API}/programs/drafts/current`, {
        program_type: normalizeProgramType(programDetails.program_type),
        current_step: currentStep,
        data: { programWizardVersion: PROGRAM_WIZARD_VERSION, programDetails, leagueSettings, divisions, paymentPlans, earlyBirdDiscount, documents, addons, emailSettings, waivers, confirmationEmail },
      }, { headers: { Authorization: `Bearer ${token}` } });
      setServerDraftSavedAt(response.data?.updated_at || new Date().toISOString());
      toast.success("Program setup saved. You can resume it from this device or another one.");
      navigate("/admin/programs");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save this setup. Your local recovery copy is still available.");
    } finally {
      setServerDraftSaving(false);
    }
  };

  // League Step Renderers
  // ==================== SIMPLIFIED LEAGUE RENDER FUNCTIONS ====================
  
  const renderLeagueDetails = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-black mb-4">League Details</h3>
      
      {/* Show league program type badge */}
      <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-xl">
        <span className="text-xl">📊</span>
        <span className="font-medium text-slate-700">League Program</span>
        <button 
          onClick={() => setCurrentStep(1)} 
          className="ml-auto text-xs text-blue-600 hover:text-blue-700"
        >
          Change Type
        </button>
      </div>
      
      {user?.role === "super_admin" && (
        <div>
          <Label>Organization *</Label>
          <Select 
            value={programDetails.org_id} 
            onValueChange={(value) => { setProgramDetails({ ...programDetails, org_id: value }); clearFieldIssue("org_id"); }}
          >
            <SelectTrigger className="mt-1 border-gray-300" {...fieldErrorProps("org_id")}>
              <SelectValue placeholder="Select organization" />
            </SelectTrigger>
            <SelectContent>
              {organizations?.map(org => (
                <SelectItem key={org.org_id} value={org.org_id}>{org.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {renderFieldError("org_id")}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label>League Name *</Label>
          <Input
            {...fieldErrorProps("name")}
            className="mt-1 border-gray-300"
            placeholder="e.g., Spring Soccer League 2026"
            value={programDetails.name}
            onChange={(e) => { setProgramDetails({ ...programDetails, name: e.target.value }); clearFieldIssue("name"); }}
            data-testid="league-name-input"
          />
          {renderFieldError("name")}
        </div>

        <div className="sm:col-span-2">
          <Label>Sport *</Label>
          <Select 
            value={programDetails.sport} 
            onValueChange={(value) => { setProgramDetails({ ...programDetails, sport: value }); clearFieldIssue("sport"); }}
          >
            <SelectTrigger className="mt-1 border-gray-300" data-testid="sport-select" {...fieldErrorProps("sport")}>
              <SelectValue placeholder="Select sport" />
            </SelectTrigger>
            <SelectContent>
              {sports.map(sport => (
                <SelectItem key={sport} value={sport}>{sport}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {renderFieldError("sport")}
        </div>

        <div>
          <Label>Start Date *</Label>
          <Input
            {...fieldErrorProps("start_date")}
            type="date"
            className="mt-1 border-gray-300"
            value={programDetails.start_date}
            onChange={(e) => { setProgramDetails({ ...programDetails, start_date: e.target.value }); clearFieldIssue("start_date"); }}
            data-testid="start-date-input"
          />
          {renderFieldError("start_date")}
        </div>
        <div>
          <Label>End Date *</Label>
          <Input
            {...fieldErrorProps("end_date")}
            type="date"
            className="mt-1 border-gray-300"
            value={programDetails.end_date}
            onChange={(e) => { setProgramDetails({ ...programDetails, end_date: e.target.value }); clearFieldIssue("end_date"); }}
            data-testid="end-date-input"
          />
          {renderFieldError("end_date")}
        </div>

        <div>
          <Label>Registration Opens</Label>
          <Input
            {...fieldErrorProps("registration_open_date")}
            type="date"
            className="mt-1 border-gray-300"
            value={programDetails.registration_open_date}
            onChange={(e) => { setProgramDetails({ ...programDetails, registration_open_date: e.target.value }); clearFieldIssue("registration_open_date"); }}
          />
          {renderFieldError("registration_open_date")}
        </div>
        <div>
          <Label>Registration Deadline</Label>
          <Input
            {...fieldErrorProps("registration_deadline")}
            type="date"
            className="mt-1 border-gray-300"
            value={programDetails.registration_deadline}
            onChange={(e) => { setProgramDetails({ ...programDetails, registration_deadline: e.target.value }); clearFieldIssue("registration_deadline"); }}
          />
          {renderFieldError("registration_deadline")}
        </div>

        <div className="sm:col-span-2">
          <Label>Location/Venue Name</Label>
          <Input
            className="mt-1 border-gray-300"
            placeholder="e.g., City Sports Complex"
            value={programDetails.location}
            onChange={(e) => setProgramDetails({ ...programDetails, location: e.target.value })}
          />
        </div>

        <div className="sm:col-span-2">
          <Label>Venue Address</Label>
          <AddressAutocomplete
            value={programDetails.venue_address}
            onChange={(value) => setProgramDetails({ ...programDetails, venue_address: value })}
            placeholder="Enter venue address"
            className="mt-1"
          />
        </div>
      </div>

      <div>
        <Label>Description</Label>
        <Textarea
          className="mt-1 border-gray-300 min-h-[100px]"
          placeholder="Describe your league..."
          value={programDetails.description}
          onChange={(e) => setProgramDetails({ ...programDetails, description: e.target.value })}
        />
      </div>

      {/* League Logo */}
      <div className="border border-dashed border-gray-300 rounded-xl p-6 text-center">
        <Image className="w-10 h-10 text-gray-400 mx-auto mb-3" />
        <p className="text-sm text-gray-500 mb-2">Upload a league logo (optional)</p>
        {programDetails.logo_url ? (
          <div className="flex items-center justify-center gap-2">
            <img src={programDetails.logo_url} alt="Logo" className="w-16 h-16 object-contain rounded-lg" />
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setProgramDetails({ ...programDetails, logo_url: "" })}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div>
            <Input
              type="file"
              accept="image/*"
              className="hidden"
              id="league-logo-upload"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setCropperImage(URL.createObjectURL(file));
                  setCropperField("logo_url");
                  setCropperOpen(true);
                }
              }}
            />
            <Button variant="outline" size="sm" onClick={() => document.getElementById("league-logo-upload").click()}>
              Upload Logo
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  const renderLeagueRegistrationType = () => (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-black">Registration Type</h3>
        <p className="text-sm text-gray-500">How will players join your league?</p>
      </div>

      {/* Registration Type Selection */}
      <div className="grid grid-cols-1 gap-4">
        <div
          onClick={() => setProgramDetails({ ...programDetails, registration_type: "individual" })}
          className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${
            programDetails.registration_type === "individual"
              ? 'border-indigo-600 bg-indigo-50'
              : 'border-slate-200 hover:border-slate-300 bg-white'
          }`}
          data-testid="reg-type-individual"
        >
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              programDetails.registration_type === "individual" ? 'bg-indigo-600 text-white' : 'bg-slate-100'
            }`}>
              <Users className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-900">Individual Registration</h4>
              <p className="text-sm text-slate-500 mt-1">
                Parents register individual players. You build teams after registration closes.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Draft picks</span>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Balanced teams</span>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Friend requests</span>
              </div>
            </div>
            {programDetails.registration_type === "individual" && (
              <Check className="w-6 h-6 text-indigo-600" />
            )}
          </div>
        </div>

        <div
          onClick={() => setProgramDetails({ ...programDetails, registration_type: "team" })}
          className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${
            programDetails.registration_type === "team"
              ? 'border-indigo-600 bg-indigo-50'
              : 'border-slate-200 hover:border-slate-300 bg-white'
          }`}
          data-testid="reg-type-team"
        >
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              programDetails.registration_type === "team" ? 'bg-indigo-600 text-white' : 'bg-slate-100'
            }`}>
              <Trophy className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-900">Team Registration</h4>
              <p className="text-sm text-slate-500 mt-1">
                Coaches register entire teams. Each team pays one registration fee.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Existing teams</span>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Travel leagues</span>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Coach managed</span>
              </div>
            </div>
            {programDetails.registration_type === "team" && (
              <Check className="w-6 h-6 text-indigo-600" />
            )}
          </div>
        </div>

        <div
          onClick={() => setProgramDetails({ ...programDetails, registration_type: "hybrid" })}
          className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${
            programDetails.registration_type === "hybrid"
              ? 'border-indigo-600 bg-indigo-50'
              : 'border-slate-200 hover:border-slate-300 bg-white'
          }`}
          data-testid="reg-type-hybrid"
        >
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              programDetails.registration_type === "hybrid" ? 'bg-indigo-600 text-white' : 'bg-slate-100'
            }`}>
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-900">Hybrid (Teams + Free Agents)</h4>
              <p className="text-sm text-slate-500 mt-1">
                Accept both team and individual registrations. Free agents get drafted to fill roster spots.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Flexible</span>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Free agents</span>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Team + individuals</span>
              </div>
            </div>
            {programDetails.registration_type === "hybrid" && (
              <Check className="w-6 h-6 text-indigo-600" />
            )}
          </div>
        </div>
      </div>

      {/* Roster Settings - shown for team and hybrid */}
      {(programDetails.registration_type === "team" || programDetails.registration_type === "hybrid") && (
        <div className="border border-slate-200 rounded-xl p-5 bg-slate-50">
          <h4 className="font-medium text-slate-900 mb-4">Team Roster Settings</h4>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-sm">Minimum Players per Team</Label>
              <Input
                type="number"
                className="mt-1 border-slate-300"
                placeholder="e.g., 8"
                value={programDetails.team_roster_min}
                onChange={(e) => setProgramDetails({ ...programDetails, team_roster_min: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-sm">Maximum Players per Team</Label>
              <Input
                type="number"
                className="mt-1 border-slate-300"
                placeholder="e.g., 15"
                value={programDetails.team_roster_max}
                onChange={(e) => setProgramDetails({ ...programDetails, team_roster_max: e.target.value })}
              />
            </div>
          </div>
        </div>
      )}

      {/* Friend Requests - shown for individual and hybrid */}
      {(programDetails.registration_type === "individual" || programDetails.registration_type === "hybrid") && (
        <div className="border border-blue-200 rounded-xl p-5 bg-blue-50">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="font-medium text-blue-900">Friend Requests</h4>
              <p className="text-sm text-blue-600">Allow players to request being on the same team</p>
            </div>
            <Switch
              checked={programDetails.friend_requests_enabled}
              onCheckedChange={(checked) => setProgramDetails({ 
                ...programDetails, 
                friend_requests_enabled: checked 
              })}
            />
          </div>
          
          {programDetails.friend_requests_enabled && (
            <div className="pt-3 border-t border-blue-200">
              <Label className="text-sm text-blue-700">Maximum Friend Requests</Label>
              <Input
                type="number"
                className="mt-1 border-blue-300 bg-white"
                placeholder="e.g., 2"
                value={programDetails.max_friend_requests}
                onChange={(e) => setProgramDetails({ ...programDetails, max_friend_requests: e.target.value })}
              />
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Teams, schedules, and standings are managed after league creation</p>
            <p className="mt-1 text-amber-600">
              Once you finish creating the league, go to the Program Dashboard to create teams, 
              set up divisions, build schedules, and configure standings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Tournament Details Step (simplified - complex config happens in Tournament Dashboard)
  const renderTournamentDetails = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-black mb-4">Tournament Details</h3>
      
      {/* Show tournament program type badge */}
      <div className="flex items-center gap-2 p-3 bg-purple-100 rounded-xl">
        <span className="text-xl">🎯</span>
        <span className="font-medium text-purple-700">Tournament Program</span>
        <button 
          onClick={() => setCurrentStep(1)} 
          className="ml-auto text-xs text-purple-600 hover:text-purple-700"
        >
          Change Type
        </button>
      </div>
      
      {user?.role === "super_admin" && (
        <div>
          <Label>Organization *</Label>
          <Select 
            value={programDetails.org_id} 
            onValueChange={(value) => setProgramDetails({ ...programDetails, org_id: value })}
          >
            <SelectTrigger className="mt-1 border-gray-300">
              <SelectValue placeholder="Select organization" />
            </SelectTrigger>
            <SelectContent>
              {organizations?.map(org => (
                <SelectItem key={org.org_id} value={org.org_id}>{org.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label>Tournament Name *</Label>
          <Input
            className="mt-1 border-gray-300"
            placeholder="e.g., Spring Classic 2026"
            value={programDetails.name}
            onChange={(e) => setProgramDetails({ ...programDetails, name: e.target.value })}
            data-testid="tournament-name-input"
          />
        </div>

        <div className="sm:col-span-2">
          <Label>Sport *</Label>
          <Select 
            value={programDetails.sport} 
            onValueChange={(value) => setProgramDetails({ ...programDetails, sport: value })}
          >
            <SelectTrigger className="mt-1 border-gray-300" data-testid="sport-select">
              <SelectValue placeholder="Select sport" />
            </SelectTrigger>
            <SelectContent>
              {sports.map(sport => (
                <SelectItem key={sport} value={sport}>{sport}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Start Date *</Label>
          <Input
            type="date"
            className="mt-1 border-gray-300"
            value={programDetails.start_date}
            onChange={(e) => setProgramDetails({ ...programDetails, start_date: e.target.value })}
            data-testid="start-date-input"
          />
        </div>
        <div>
          <Label>End Date *</Label>
          <Input
            type="date"
            className="mt-1 border-gray-300"
            value={programDetails.end_date}
            onChange={(e) => setProgramDetails({ ...programDetails, end_date: e.target.value })}
            data-testid="end-date-input"
          />
        </div>

        <div>
          <Label>Registration Opens</Label>
          <Input
            type="date"
            className="mt-1 border-gray-300"
            value={programDetails.registration_open_date}
            onChange={(e) => setProgramDetails({ ...programDetails, registration_open_date: e.target.value })}
          />
        </div>
        <div>
          <Label>Registration Deadline *</Label>
          <Input
            type="date"
            className="mt-1 border-gray-300"
            value={programDetails.registration_deadline}
            onChange={(e) => setProgramDetails({ ...programDetails, registration_deadline: e.target.value })}
          />
        </div>

        <div className="sm:col-span-2">
          <Label>Venue / Location *</Label>
          <Input
            className="mt-1 border-gray-300"
            placeholder="e.g., City Sports Complex"
            value={programDetails.location}
            onChange={(e) => setProgramDetails({ ...programDetails, location: e.target.value })}
          />
        </div>

        <div className="sm:col-span-2">
          <Label>Venue Address</Label>
          <Input
            className="mt-1 border-gray-300"
            placeholder="e.g., 123 Main St, City, State"
            value={programDetails.venue_address}
            onChange={(e) => setProgramDetails({ ...programDetails, venue_address: e.target.value })}
          />
        </div>

        <div className="sm:col-span-2">
          <Label>Registration Fee *</Label>
          <div className="relative mt-1">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="number"
              className="pl-9 border-gray-300"
              placeholder="0.00"
              value={programDetails.registration_fee}
              onChange={(e) => setProgramDetails({ ...programDetails, registration_fee: e.target.value })}
              data-testid="registration-fee-input"
            />
          </div>
        </div>

        <div className="sm:col-span-2">
          <Label>Description</Label>
          <Textarea
            className="mt-1 border-gray-300"
            placeholder="Describe your tournament..."
            rows={3}
            value={programDetails.description}
            onChange={(e) => setProgramDetails({ ...programDetails, description: e.target.value })}
          />
        </div>

        <div className="sm:col-span-2">
          <Label>Maximum Participants</Label>
          <Input
            type="number"
            className="mt-1 border-gray-300"
            placeholder="Leave blank for unlimited"
            value={programDetails.max_participants}
            onChange={(e) => setProgramDetails({ ...programDetails, max_participants: e.target.value })}
          />
          <p className="text-xs text-gray-500 mt-1">Maximum number of teams or players that can register</p>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mt-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-purple-600 mt-0.5" />
          <div className="text-sm text-purple-800">
            <p className="font-medium">Tournament format, divisions, brackets, and schedules are configured after creation</p>
            <p className="mt-1 text-purple-600">
              Once you finish creating the tournament, go to the Tournament Dashboard to set up 
              competition format, divisions, seeding, brackets, and game schedules.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Tournament Registration Type Step
  const renderTournamentRegistrationType = () => (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-black">Registration Type</h3>
        <p className="text-sm text-gray-500">How will participants join your tournament?</p>
      </div>

      {/* Registration Type Selection */}
      <div className="grid grid-cols-1 gap-4">
        <div
          onClick={() => setProgramDetails({ ...programDetails, registration_type: "individual" })}
          className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${
            programDetails.registration_type === "individual"
              ? 'border-purple-600 bg-purple-50'
              : 'border-slate-200 hover:border-slate-300 bg-white'
          }`}
          data-testid="tournament-reg-type-individual"
        >
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              programDetails.registration_type === "individual" ? 'bg-purple-600 text-white' : 'bg-slate-100'
            }`}>
              <Users className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-900">Individual Registration</h4>
              <p className="text-sm text-slate-500 mt-1">
                Players register individually. Great for singles tournaments or when you&apos;ll form teams later.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Singles events</span>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Draft format</span>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Seeding</span>
              </div>
            </div>
            {programDetails.registration_type === "individual" && (
              <Check className="w-6 h-6 text-purple-600" />
            )}
          </div>
        </div>

        <div
          onClick={() => setProgramDetails({ ...programDetails, registration_type: "team" })}
          className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${
            programDetails.registration_type === "team"
              ? 'border-purple-600 bg-purple-50'
              : 'border-slate-200 hover:border-slate-300 bg-white'
          }`}
          data-testid="tournament-reg-type-team"
        >
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              programDetails.registration_type === "team" ? 'bg-purple-600 text-white' : 'bg-slate-100'
            }`}>
              <Trophy className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-900">Team Registration</h4>
              <p className="text-sm text-slate-500 mt-1">
                Coaches or team managers register their entire team. Standard for most team tournaments.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Bracket play</span>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Team fee</span>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">Roster management</span>
              </div>
            </div>
            {programDetails.registration_type === "team" && (
              <Check className="w-6 h-6 text-purple-600" />
            )}
          </div>
        </div>
      </div>

      {/* Roster Settings - shown for team registration */}
      {programDetails.registration_type === "team" && (
        <div className="border border-slate-200 rounded-xl p-5 bg-slate-50">
          <h4 className="font-medium text-slate-900 mb-4">Team Roster Settings</h4>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-sm">Minimum Players per Team</Label>
              <Input
                type="number"
                className="mt-1 border-slate-300"
                placeholder="e.g., 8"
                value={programDetails.team_roster_min}
                onChange={(e) => setProgramDetails({ ...programDetails, team_roster_min: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-sm">Maximum Players per Team</Label>
              <Input
                type="number"
                className="mt-1 border-slate-300"
                placeholder="e.g., 15"
                value={programDetails.team_roster_max}
                onChange={(e) => setProgramDetails({ ...programDetails, team_roster_max: e.target.value })}
              />
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Brackets and seeding configured after tournament creation</p>
            <p className="mt-1 text-amber-600">
              Set up divisions with different price points in the next step. After creating the tournament, 
              go to the Tournament Dashboard to create brackets, manage seeding, and build game schedules.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Tournament Merchandise Step (dedicated step for tournaments)
  const renderTournamentMerchandiseStep = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-black">Merchandise</h3>
          <p className="text-sm text-gray-500">Offer tournament merchandise to participants</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Enable Merchandise</span>
          <Switch
            checked={programDetails.merchandise_enabled}
            onCheckedChange={(checked) => setProgramDetails({ 
              ...programDetails, 
              merchandise_enabled: checked 
            })}
            data-testid="merchandise-toggle"
          />
        </div>
      </div>

      {programDetails.merchandise_enabled ? (
        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Package className="w-5 h-5 text-purple-600 mt-0.5" />
              <div className="text-sm text-purple-800">
                <p className="font-medium">Tournament Merchandise Store</p>
                <p className="mt-1 text-purple-600">
                  After creating the tournament, you can set up merchandise in the Tournament Dashboard. 
                  Options include custom t-shirts, jerseys, hats, and more through our Printful integration.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 border border-slate-200 rounded-xl bg-white">
            <h4 className="font-medium text-slate-900 mb-3">Common Tournament Merchandise</h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">👕</div>
                <span>Tournament T-Shirts</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">🧢</div>
                <span>Hats & Caps</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">🏆</div>
                <span>Champion Gear</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">🎒</div>
                <span>Bags & Accessories</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-300">
          <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Enable merchandise to offer tournament swag to participants</p>
          <p className="text-sm text-slate-400 mt-1">You can always enable this later in Tournament Settings</p>
        </div>
      )}
    </div>
  );

  // Tournament Divisions & Pricing Step
  const renderTournamentDivisionsStep = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-black">Divisions & Pricing</h3>
          <p className="text-sm text-gray-500">Set up age groups/divisions with different price points</p>
        </div>
        <Button onClick={addDivision} variant="outline" size="sm" className="border-purple-300 text-purple-700 hover:bg-purple-50">
          <Plus className="w-4 h-4 mr-1" /> Add Division
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4">
        <div className="flex items-start gap-3">
          <DollarSign className="w-5 h-5 text-purple-600 mt-0.5" />
          <div className="text-sm text-purple-800">
            <p className="font-medium">Division-Based Pricing</p>
            <p className="mt-1 text-purple-600">
              Each division can have its own registration fee. For example, U8 might be $100 while U14 is $150.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {divisions.map((division, index) => (
          <div key={index} className="p-4 border border-purple-200 rounded-2xl bg-purple-50/50">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-purple-900">Division {index + 1}</span>
              {divisions.length > 1 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removeDivision(index)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="col-span-2 md:col-span-1">
                <Label className="text-sm text-purple-700">Division Name *</Label>
                <Input
                  className="mt-1 border-purple-200 focus:border-purple-400"
                  placeholder="e.g., U10, U12, U14, Open"
                  value={division.name}
                  onChange={(e) => updateDivision(index, "name", e.target.value)}
                />
              </div>

              <div className="col-span-2 md:col-span-1">
                <Label className="text-sm text-purple-700">Registration Fee ($) *</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-400" />
                  <Input
                    type="number"
                    className="pl-9 border-purple-200 focus:border-purple-400"
                    placeholder="e.g., 150"
                    value={division.price}
                    onChange={(e) => updateDivision(index, "price", e.target.value)}
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm text-purple-700">Oldest Birth Date</Label>
                <Input
                  type="date"
                  className="mt-1 border-purple-200 focus:border-purple-400"
                  value={division.birth_date_start}
                  onChange={(e) => updateDivision(index, "birth_date_start", e.target.value)}
                />
                <p className="text-xs text-purple-500 mt-1">Earliest birth date allowed</p>
              </div>

              <div>
                <Label className="text-sm text-purple-700">Youngest Birth Date</Label>
                <Input
                  type="date"
                  className="mt-1 border-purple-200 focus:border-purple-400"
                  value={division.birth_date_end}
                  onChange={(e) => updateDivision(index, "birth_date_end", e.target.value)}
                />
                <p className="text-xs text-purple-500 mt-1">Latest birth date allowed</p>
              </div>

              <div>
                <Label className="text-sm text-purple-700">Gender</Label>
                <Select 
                  value={division.gender} 
                  onValueChange={(value) => updateDivision(index, "gender", value)}
                >
                  <SelectTrigger className="mt-1 border-purple-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="coed">Co-ed</SelectItem>
                    <SelectItem value="boys">Boys / Men</SelectItem>
                    <SelectItem value="girls">Girls / Women</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm text-purple-700">Max Teams/Players</Label>
                <Input
                  type="number"
                  className="mt-1 border-purple-200 focus:border-purple-400"
                  placeholder="Leave empty for unlimited"
                  value={division.max_players}
                  onChange={(e) => updateDivision(index, "max_players", e.target.value)}
                />
              </div>
            </div>

            {/* Price Display */}
            {division.name && division.price && (
              <div className="mt-3 pt-3 border-t border-purple-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-purple-600">Price for {division.name}:</span>
                  <span className="font-semibold text-purple-900">${parseFloat(division.price).toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pricing Summary */}
      {divisions.length > 1 && divisions.some(d => d.price) && (
        <div className="mt-4 p-4 bg-slate-100 rounded-xl border border-slate-200">
          <h4 className="font-medium text-slate-800 mb-2">Pricing Summary</h4>
          <div className="space-y-1">
            {divisions.filter(d => d.name && d.price).map((d, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-slate-600">{d.name}</span>
                <span className="font-medium text-slate-800">${parseFloat(d.price).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Note about bracket configuration */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Brackets and seeding configured after creation</p>
            <p className="mt-1 text-amber-600">
              After creating the tournament, you can set up brackets, manage seeding, and build 
              game schedules for each division in the Tournament Dashboard.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Helper functions for extracting step content
  const renderDivisionsStep = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-black">Divisions & Pricing</h3>
          <p className="text-sm text-gray-500">Create age groups with different price points for your league</p>
        </div>
        <Button onClick={addDivision} variant="outline" size="sm" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
          <Plus className="w-4 h-4 mr-1" /> Add Division
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-4">
        <div className="flex items-start gap-3">
          <DollarSign className="w-5 h-5 text-indigo-600 mt-0.5" />
          <div className="text-sm text-indigo-800">
            <p className="font-medium">Division-Based Pricing</p>
            <p className="mt-1 text-indigo-600">
              Each division can have its own registration fee. For example, U8 might be $100 while U14 is $150.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {divisions.map((division, index) => (
          <div key={index} className="p-4 border border-indigo-200 rounded-2xl bg-indigo-50/50">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-indigo-900">Division {index + 1}</span>
              {divisions.length > 1 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removeDivision(index)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="col-span-2 md:col-span-1">
                <Label className="text-sm text-indigo-700">Division Name *</Label>
                <Input
                  className="mt-1 border-indigo-200 focus:border-indigo-400"
                  placeholder="e.g., U10, Varsity"
                  value={division.name}
                  onChange={(e) => updateDivision(index, "name", e.target.value)}
                />
              </div>

              <div className="col-span-2 md:col-span-1">
                <Label className="text-sm text-indigo-700">Registration Fee ($) *</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-400" />
                  <Input
                    type="number"
                    className="pl-9 border-indigo-200 focus:border-indigo-400"
                    placeholder="e.g., 150"
                    value={division.price}
                    onChange={(e) => updateDivision(index, "price", e.target.value)}
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm text-indigo-700">Oldest Birth Date</Label>
                <Input
                  type="date"
                  className="mt-1 border-indigo-200 focus:border-indigo-400"
                  value={division.birth_date_start}
                  onChange={(e) => updateDivision(index, "birth_date_start", e.target.value)}
                />
                <p className="text-xs text-indigo-500 mt-1">Earliest birth date allowed</p>
              </div>

              <div>
                <Label className="text-sm text-indigo-700">Youngest Birth Date</Label>
                <Input
                  type="date"
                  className="mt-1 border-indigo-200 focus:border-indigo-400"
                  value={division.birth_date_end}
                  onChange={(e) => updateDivision(index, "birth_date_end", e.target.value)}
                />
                <p className="text-xs text-indigo-500 mt-1">Latest birth date allowed</p>
              </div>

              <div>
                <Label className="text-sm text-indigo-700">Gender</Label>
                <Select 
                  value={division.gender} 
                  onValueChange={(value) => updateDivision(index, "gender", value)}
                >
                  <SelectTrigger className="mt-1 border-indigo-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="coed">Co-ed</SelectItem>
                    <SelectItem value="boys">Boys</SelectItem>
                    <SelectItem value="girls">Girls</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm text-indigo-700">Max Participants</Label>
                <Input
                  type="number"
                  className="mt-1 border-indigo-200 focus:border-indigo-400"
                  placeholder="Leave empty for unlimited"
                  value={division.max_players}
                  onChange={(e) => updateDivision(index, "max_players", e.target.value)}
                />
              </div>
            </div>

            {/* Price Display */}
            {division.name && division.price && (
              <div className="mt-3 pt-3 border-t border-indigo-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-indigo-600">Price for {division.name}:</span>
                  <span className="font-semibold text-indigo-900">${parseFloat(division.price).toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pricing Summary */}
      {divisions.length > 1 && divisions.some(d => d.price) && (
        <div className="mt-4 p-4 bg-slate-100 rounded-xl border border-slate-200">
          <h4 className="font-medium text-slate-800 mb-2">Pricing Summary</h4>
          <div className="space-y-1">
            {divisions.filter(d => d.name && d.price).map((d, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-slate-600">{d.name}</span>
                <span className="font-medium text-slate-800">${parseFloat(d.price).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderDocumentsStep = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-black">Waivers & Documents</h3>
          <p className="text-sm text-gray-500">Add required documents and waivers for registration</p>
        </div>
        <Button onClick={addWaiver} variant="outline" size="sm" className="border-gray-300">
          <Plus className="w-4 h-4 mr-1" /> Add Document
        </Button>
      </div>

      {waivers.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-2xl">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p>No documents added yet</p>
          <p className="text-sm">Click &quot;Add Document&quot; to require waivers or forms</p>
        </div>
      ) : (
        <div className="space-y-3">
          {waivers.map((waiver, index) => (
            <div key={index} className="p-4 border border-gray-200 rounded-2xl bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-black">Document {index + 1}</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removeWaiver(index)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Document Title *</Label>
                  <Input
                    className="mt-1 border-gray-300"
                    placeholder="e.g., Liability Waiver"
                    value={waiver.title}
                    onChange={(e) => updateWaiver(index, "title", e.target.value)}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-sm">Content</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-indigo-600 hover:text-indigo-700"
                      onClick={() => generateWaiverContent(index)}
                      disabled={aiLoading[`waiver_${index}`]}
                    >
                      {aiLoading[`waiver_${index}`] ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <Sparkles className="w-3 h-3 mr-1" />
                      )}
                      Generate with AI
                    </Button>
                  </div>
                  <RichTextEditor
                    value={waiver.content}
                    onChange={(content) => updateWaiver(index, "content", content)}
                    placeholder="Enter waiver/document content..."
                    className="min-h-[150px]"
                  />
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <Checkbox
                    id={`waiver-required-${index}`}
                    checked={waiver.is_required}
                    onCheckedChange={(checked) => updateWaiver(index, "is_required", checked)}
                  />
                  <Label htmlFor={`waiver-required-${index}`} className="text-sm font-normal cursor-pointer">
                    Required for registration
                  </Label>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderAddonsStep = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-black">Add-ons</h3>
          <p className="text-sm text-gray-500">Optional items parents can purchase during registration</p>
        </div>
        <Button onClick={addAddon} variant="outline" size="sm" className="border-gray-300">
          <Plus className="w-4 h-4 mr-1" /> Add Item
        </Button>
      </div>

      {/* Merchandise Toggle */}
      <div className="p-4 border border-purple-200 rounded-2xl bg-purple-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h4 className="font-medium text-purple-900">Merchandise</h4>
              <p className="text-sm text-purple-600">Allow parents to purchase branded merchandise</p>
            </div>
          </div>
          <Switch
            checked={programDetails.show_merchandise_in_registration}
            onCheckedChange={(checked) => setProgramDetails({ 
              ...programDetails, 
              show_merchandise_in_registration: checked 
            })}
          />
        </div>
        {programDetails.show_merchandise_in_registration && (
          <p className="text-xs text-purple-500 mt-2 ml-13">
            Merchandise items are configured in Organization Settings → Merchandise
          </p>
        )}
      </div>

      {/* Custom Add-ons */}
      {addons.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-2xl">
          <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p>No custom add-ons</p>
          <p className="text-sm">Click &quot;Add Item&quot; to create optional purchases</p>
        </div>
      ) : (
        <div className="space-y-3">
          {addons.map((addon, index) => (
            <div key={index} className="p-4 border border-gray-200 rounded-2xl bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-black">Add-on {index + 1}</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removeAddon(index)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label className="text-sm">Name *</Label>
                  <Input
                    className="mt-1 border-gray-300"
                    placeholder="e.g., Team Jersey"
                    value={addon.name}
                    onChange={(e) => updateAddon(index, "name", e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-sm">Price ($) *</Label>
                  <Input
                    type="number"
                    className="mt-1 border-gray-300"
                    placeholder="0"
                    value={addon.price}
                    onChange={(e) => updateAddon(index, "price", e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-sm">Type</Label>
                  <Select 
                    value={addon.type || "optional"} 
                    onValueChange={(value) => updateAddon(index, "type", value)}
                  >
                    <SelectTrigger className="mt-1 border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="optional">Optional</SelectItem>
                      <SelectItem value="required">Required</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-sm">Description</Label>
                  <Input
                    className="mt-1 border-gray-300"
                    placeholder="Brief description"
                    value={addon.description}
                    onChange={(e) => updateAddon(index, "description", e.target.value)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderPaymentPlansStep = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-black">Payment Plans</h3>
          <p className="text-sm text-gray-500">Configure payment options for registrations</p>
        </div>
        <Button onClick={addPaymentPlan} variant="outline" size="sm" className="border-gray-300">
          <Plus className="w-4 h-4 mr-1" /> Add Plan
        </Button>
      </div>

      <div className="space-y-3">
        {paymentPlans.map((plan, index) => (
          <div key={index} className="p-4 border border-gray-200 rounded-2xl bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="font-medium text-black">{plan.name || `Plan ${index + 1}`}</span>
                {plan.is_default && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Default</span>
                )}
              </div>
              {paymentPlans.length > 1 && !plan.is_required && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removePaymentPlan(index)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-sm">Plan Name</Label>
                <Input
                  className="mt-1 border-gray-300"
                  placeholder="e.g., Full Payment"
                  value={plan.name}
                  onChange={(e) => updatePaymentPlan(index, "name", e.target.value)}
                  disabled={plan.is_required}
                />
              </div>
              <div>
                <Label className="text-sm"># of Payments</Label>
                <Input
                  type="number"
                  className="mt-1 border-gray-300"
                  min="1"
                  value={plan.num_payments}
                  onChange={(e) => updatePaymentPlan(index, "num_payments", parseInt(e.target.value) || 1)}
                  disabled={plan.is_required}
                />
              </div>
              <div>
                <Label className="text-sm">Deposit Amount ($)</Label>
                <Input
                  type="number"
                  className="mt-1 border-gray-300"
                  placeholder="0"
                  value={plan.deposit}
                  onChange={(e) => updatePaymentPlan(index, "deposit", e.target.value)}
                  disabled={plan.num_payments === 1}
                />
              </div>
            </div>

            {!plan.is_required && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                <Checkbox
                  id={`plan-default-${index}`}
                  checked={plan.is_default}
                  onCheckedChange={() => setDefaultPaymentPlan(index)}
                />
                <Label htmlFor={`plan-default-${index}`} className="text-sm font-normal cursor-pointer">
                  Set as default option
                </Label>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderEmailStep = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-black mb-4">Confirmation Email</h3>
      <p className="text-sm text-gray-500 mb-4">
        Customize the email sent to parents after they complete registration.
      </p>

      <div>
        <div className="flex items-center justify-between mb-1">
          <Label>Email Subject</Label>
        </div>
        <Input
          {...fieldErrorProps("confirmation_subject")}
          className="border-gray-300"
          placeholder="Registration Confirmation - {{program_name}}"
          value={confirmationEmail.subject}
          onChange={(e) => { setConfirmationEmail({ ...confirmationEmail, subject: e.target.value }); clearFieldIssue("confirmation_subject"); }}
        />
        {renderFieldError("confirmation_subject")}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <Label>Email Body</Label>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-indigo-600 hover:text-indigo-700"
            onClick={generateEmailContent}
            disabled={aiLoading.email}
          >
            {aiLoading.email ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Sparkles className="w-3 h-3 mr-1" />
            )}
            Generate with AI
          </Button>
        </div>
        <RichTextEditor
          {...fieldErrorProps("confirmation_body")}
          value={confirmationEmail.body}
          onChange={(content) => { setConfirmationEmail({ ...confirmationEmail, body: content }); clearFieldIssue("confirmation_body"); }}
          placeholder="Dear {{parent_name}}, Thank you for registering {{player_name}}..."
          className="min-h-[200px]"
        />
        {renderFieldError("confirmation_body")}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm font-medium text-blue-900 mb-2">Available Variables:</p>
        <div className="flex flex-wrap gap-2">
          {["{{parent_name}}", "{{player_name}}", "{{program_name}}", "{{division_name}}", "{{start_date}}", "{{amount_paid}}"].map((tag) => (
            <code key={tag} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
              {tag}
            </code>
          ))}
        </div>
      </div>
    </div>
  );

  // Keep old render functions for backward compatibility but they won't be used for league anymore
  const renderLeagueStructure = () => (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-black">League Structure</h3>
        <p className="text-sm text-gray-500">Configure how your league will be organized</p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        {/* League Format */}
        <div>
          <Label>League Format *</Label>
          <Select 
            value={programDetails.league_format} 
            onValueChange={(value) => setProgramDetails({ ...programDetails, league_format: value })}
          >
            <SelectTrigger className="mt-1 border-gray-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="round_robin">Round Robin (everyone plays everyone)</SelectItem>
              <SelectItem value="double_round_robin">Double Round Robin</SelectItem>
              <SelectItem value="conference">Conference/Pool Play</SelectItem>
              <SelectItem value="custom">Custom Schedule</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Expected Teams */}
        <div>
          <Label>Expected Number of Teams</Label>
          <Input
            type="number"
            className="mt-1 border-gray-300"
            placeholder="e.g., 8"
            value={programDetails.league_teams_count}
            onChange={(e) => setProgramDetails({ ...programDetails, league_teams_count: e.target.value })}
          />
        </div>

        {/* Games Per Team */}
        <div>
          <Label>Target Games Per Team</Label>
          <Input
            type="number"
            className="mt-1 border-gray-300"
            placeholder="e.g., 10"
            value={programDetails.games_per_team}
            onChange={(e) => setProgramDetails({ ...programDetails, games_per_team: e.target.value })}
          />
        </div>

        {/* Registration Type */}
        <div>
          <Label>Registration Type *</Label>
          <Select 
            value={programDetails.registration_type} 
            onValueChange={(value) => setProgramDetails({ ...programDetails, registration_type: value })}
          >
            <SelectTrigger className="mt-1 border-gray-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="individual">Individual Players (system forms teams)</SelectItem>
              <SelectItem value="team">Team Registration (coaches register teams)</SelectItem>
              <SelectItem value="hybrid">Hybrid (teams + free agents)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Team Roster Limits */}
      <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
        <h4 className="font-medium text-black mb-3">Team Roster Limits</h4>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-sm">Minimum Players</Label>
            <Input
              type="number"
              className="mt-1 border-gray-300"
              placeholder="e.g., 8"
              value={programDetails.team_roster_min}
              onChange={(e) => setProgramDetails({ ...programDetails, team_roster_min: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-sm">Maximum Players</Label>
            <Input
              type="number"
              className="mt-1 border-gray-300"
              placeholder="e.g., 15"
              value={programDetails.team_roster_max}
              onChange={(e) => setProgramDetails({ ...programDetails, team_roster_max: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* Playoffs */}
      <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-medium text-black">Playoffs</h4>
            <p className="text-sm text-gray-500">Enable post-season playoffs</p>
          </div>
          <Checkbox
            checked={programDetails.playoffs_enabled}
            onCheckedChange={(checked) => setProgramDetails({ ...programDetails, playoffs_enabled: checked })}
          />
        </div>
        
        {programDetails.playoffs_enabled && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mt-4 pt-4 border-t border-gray-200">
            <div>
              <Label className="text-sm">Teams Qualifying</Label>
              <Input
                type="number"
                className="mt-1 border-gray-300"
                placeholder="e.g., 4"
                value={programDetails.playoff_teams}
                onChange={(e) => setProgramDetails({ ...programDetails, playoff_teams: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-sm">Playoff Format</Label>
              <Select 
                value={programDetails.playoff_format} 
                onValueChange={(value) => setProgramDetails({ ...programDetails, playoff_format: value })}
              >
                <SelectTrigger className="mt-1 border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single_elimination">Single Elimination</SelectItem>
                  <SelectItem value="double_elimination">Double Elimination</SelectItem>
                  <SelectItem value="best_of_3">Best of 3</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderLeagueScheduling = () => {
    const weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    
    const toggleGameDay = (day) => {
      const newDays = leagueSettings.game_days.includes(day)
        ? leagueSettings.game_days.filter(d => d !== day)
        : [...leagueSettings.game_days, day];
      setLeagueSettings({ ...leagueSettings, game_days: newDays });
    };

    const addVenue = () => {
      setLeagueSettings({ ...leagueSettings, venues: [...leagueSettings.venues, ""] });
    };

    const updateVenue = (index, value) => {
      const newVenues = [...leagueSettings.venues];
      newVenues[index] = value;
      setLeagueSettings({ ...leagueSettings, venues: newVenues });
    };

    const removeVenue = (index) => {
      if (leagueSettings.venues.length > 1) {
        setLeagueSettings({ ...leagueSettings, venues: leagueSettings.venues.filter((_, i) => i !== index) });
      }
    };

    const addTimeSlot = () => {
      setLeagueSettings({ ...leagueSettings, time_slots: [...leagueSettings.time_slots, ""] });
    };

    const updateTimeSlot = (index, value) => {
      const newSlots = [...leagueSettings.time_slots];
      newSlots[index] = value;
      setLeagueSettings({ ...leagueSettings, time_slots: newSlots });
    };

    const removeTimeSlot = (index) => {
      if (leagueSettings.time_slots.length > 1) {
        setLeagueSettings({ ...leagueSettings, time_slots: leagueSettings.time_slots.filter((_, i) => i !== index) });
      }
    };

    return (
      <div className="space-y-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-black">Scheduling Configuration</h3>
          <p className="text-sm text-gray-500">Set up when and where games will be played</p>
        </div>

        {/* Game Days */}
        <div>
          <Label className="mb-2 block">Game Days *</Label>
          <div className="flex flex-wrap gap-2">
            {weekDays.map((day) => (
              <button
                key={day}
                type="button"
                onClick={() => toggleGameDay(day)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  leagueSettings.game_days.includes(day)
                    ? 'bg-slate-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {day}
              </button>
            ))}
          </div>
        </div>

        {/* Time Slots */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Available Time Slots</Label>
            <Button onClick={addTimeSlot} variant="outline" size="sm" className="border-gray-300">
              <Plus className="w-4 h-4 mr-1" /> Add Time
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {leagueSettings.time_slots.map((slot, index) => (
              <div key={index} className="flex items-center gap-1">
                <Input
                  type="time"
                  className="border-gray-300 text-sm"
                  value={slot}
                  onChange={(e) => updateTimeSlot(index, e.target.value)}
                />
                {leagueSettings.time_slots.length > 1 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => removeTimeSlot(index)}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Game Duration */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label>Game Duration (minutes)</Label>
            <Input
              type="number"
              className="mt-1 border-gray-300"
              placeholder="e.g., 60"
              value={leagueSettings.game_duration}
              onChange={(e) => setLeagueSettings({ ...leagueSettings, game_duration: e.target.value })}
            />
          </div>
        </div>

        {/* Venues */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Venues / Facilities</Label>
            <Button onClick={addVenue} variant="outline" size="sm" className="border-gray-300">
              <Plus className="w-4 h-4 mr-1" /> Add Venue
            </Button>
          </div>
          <div className="space-y-2">
            {leagueSettings.venues.map((venue, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  className="border-gray-300"
                  placeholder="e.g., Central Park Field 1"
                  value={venue}
                  onChange={(e) => updateVenue(index, e.target.value)}
                />
                {leagueSettings.venues.length > 1 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => removeVenue(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Blackout Dates Note */}
        <div className="border border-amber-200 rounded-xl p-4 bg-amber-50">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-900">Blackout Dates</h4>
              <p className="text-sm text-amber-700">
                After creating the league, you can set specific blackout dates (holidays, breaks) 
                in the Schedule Builder from the program dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLeagueStandings = () => {
    const tiebreakerOptions = [
      { value: "head_to_head", label: "Head-to-Head" },
      { value: "goal_differential", label: "Goal Differential" },
      { value: "goals_for", label: "Goals For" },
      { value: "goals_against", label: "Goals Against" },
      { value: "wins", label: "Total Wins" },
    ];

    const statOptions = [
      { value: "goals", label: "Goals" },
      { value: "assists", label: "Assists" },
      { value: "saves", label: "Saves" },
      { value: "yellow_cards", label: "Yellow Cards" },
      { value: "red_cards", label: "Red Cards" },
    ];

    const toggleTiebreaker = (value) => {
      const newTiebreakers = leagueSettings.tiebreakers.includes(value)
        ? leagueSettings.tiebreakers.filter(t => t !== value)
        : [...leagueSettings.tiebreakers, value];
      setLeagueSettings({ ...leagueSettings, tiebreakers: newTiebreakers });
    };

    const toggleStat = (value) => {
      const newStats = leagueSettings.stats_to_track.includes(value)
        ? leagueSettings.stats_to_track.filter(s => s !== value)
        : [...leagueSettings.stats_to_track, value];
      setLeagueSettings({ ...leagueSettings, stats_to_track: newStats });
    };

    return (
      <div className="space-y-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-black">Standings & Rules</h3>
          <p className="text-sm text-gray-500">Configure how standings are calculated and displayed</p>
        </div>

        {/* Points System */}
        <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
          <h4 className="font-medium text-black mb-3">Points System</h4>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Label className="text-sm">Points for Win</Label>
              <Input
                type="number"
                className="mt-1 border-gray-300"
                value={leagueSettings.points_win}
                onChange={(e) => setLeagueSettings({ ...leagueSettings, points_win: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-sm">Points for Tie</Label>
              <Input
                type="number"
                className="mt-1 border-gray-300"
                value={leagueSettings.points_tie}
                onChange={(e) => setLeagueSettings({ ...leagueSettings, points_tie: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-sm">Points for Loss</Label>
              <Input
                type="number"
                className="mt-1 border-gray-300"
                value={leagueSettings.points_loss}
                onChange={(e) => setLeagueSettings({ ...leagueSettings, points_loss: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Tiebreakers */}
        <div>
          <Label className="mb-2 block">Tiebreaker Rules (in order of priority)</Label>
          <div className="flex flex-wrap gap-2">
            {tiebreakerOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => toggleTiebreaker(option.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  leagueSettings.tiebreakers.includes(option.value)
                    ? 'bg-slate-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {leagueSettings.tiebreakers.includes(option.value) && (
                  <span className="mr-1">{leagueSettings.tiebreakers.indexOf(option.value) + 1}.</span>
                )}
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stats Tracking */}
        <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="font-medium text-black">Track Player Statistics</h4>
              <p className="text-sm text-gray-500">Enable individual player stat tracking</p>
            </div>
            <Checkbox
              checked={leagueSettings.track_stats}
              onCheckedChange={(checked) => setLeagueSettings({ ...leagueSettings, track_stats: checked })}
            />
          </div>
          
          {leagueSettings.track_stats && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <Label className="text-sm mb-2 block">Stats to Track</Label>
              <div className="flex flex-wrap gap-2">
                {statOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggleStat(option.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      leagueSettings.stats_to_track.includes(option.value)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Public Display */}
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl">
          <div>
            <h4 className="font-medium text-black">Display Standings Publicly</h4>
            <p className="text-sm text-gray-500">Show standings on the public program page</p>
          </div>
          <Checkbox
            checked={leagueSettings.display_standings_publicly}
            onCheckedChange={(checked) => setLeagueSettings({ ...leagueSettings, display_standings_publicly: checked })}
          />
        </div>

        {/* League Rules */}
        <div>
          <Label>League Rules Document (Optional)</Label>
          <p className="text-sm text-gray-500 mb-2">Add any specific rules for your league</p>
          <RichTextEditor
            value={leagueSettings.rules_document}
            onChange={(value) => setLeagueSettings({ ...leagueSettings, rules_document: value })}
            placeholder="Enter league rules, code of conduct, etc..."
          />
        </div>
      </div>
    );
  };

  const renderReviewStep = () => {
    const readiness = getReadiness();
    const stepFor = isLeague
      ? { basics: 2, divisions: 4, payments: 6, documents: 5, addons: 5, email: 7 }
      : { basics: 2, divisions: 3, payments: 4, documents: 5, addons: 5, email: 6 };
    const reviewEmail = getActiveProgramEmailSettings({ isLeague, emailSettings, confirmationEmail });
    return (
      <div className="space-y-6" data-testid="program-readiness-review">
        <div>
          <h3 className="text-2xl font-bold text-slate-900">Review and launch</h3>
          <p className="text-sm text-slate-500 mt-1">Confirm what families will see, when registration opens, what they will pay, and which message they receive.</p>
        </div>

        <div className={`rounded-2xl border p-5 ${readiness.ready ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
          <div className="flex items-start gap-3">
            <div className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-full ${readiness.ready ? "bg-emerald-600" : "bg-amber-500"}`}>
              {readiness.ready ? <Check className="h-4 w-4 text-white" /> : <Info className="h-4 w-4 text-white" />}
            </div>
            <div>
              <h4 className="font-semibold text-slate-900">{readiness.ready ? "Ready to publish" : `${readiness.issues.length} item${readiness.issues.length === 1 ? "" : "s"} need attention`}</h4>
              <p className="text-sm text-slate-600">{readiness.ready ? "All launch requirements are complete." : "Open an item below to correct it before publishing."}</p>
            </div>
          </div>
          {!readiness.ready && (
            <div className="mt-4 space-y-2">
              {readiness.issues.map((issue, index) => (
                <button key={`${issue.step}-${issue.label}-${index}`} type="button" onClick={() => setCurrentStep(issue.step)} className="flex w-full items-center justify-between rounded-xl border border-amber-200 bg-white px-4 py-3 text-left text-sm text-slate-700 hover:border-amber-400">
                  <span>{issue.label}</span><span className="text-xs font-medium text-amber-700">Open {STEPS.find((step) => step.id === issue.step)?.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <ProgramLaunchChecklist
          programDetails={programDetails}
          divisions={divisions}
          paymentPlans={paymentPlans}
          documents={isLeague ? waivers : documents}
          addons={addons}
          emailSettings={reviewEmail}
          stepFor={stepFor}
          onEdit={goToStep}
        />

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
          <h4 className="font-semibold text-slate-900">What publishing does</h4>
          <dl className="mt-4 grid gap-4 text-sm sm:grid-cols-2">
            <div><dt className="font-medium text-slate-700">Public listing</dt><dd className="mt-1 text-slate-500">Creates the public registration page for this program.</dd></div>
            <div><dt className="font-medium text-slate-700">Registration timing</dt><dd className="mt-1 text-slate-500">{programDetails.registration_open_date ? `Opens ${programDetails.registration_open_date}` : "Opens as soon as the program is published"} and closes {programDetails.registration_deadline || "when a deadline is added"}.</dd></div>
            <div><dt className="font-medium text-slate-700">Who can enroll</dt><dd className="mt-1 text-slate-500">Families whose participants meet a configured division’s birth-date and availability rules.</dd></div>
            <div><dt className="font-medium text-slate-700">Payment</dt><dd className="mt-1 text-slate-500">Charges the selected division price{paymentPlans.length > 1 ? ` with ${paymentPlans.length} available payment options` : " using full payment"}.</dd></div>
            <div className="sm:col-span-2"><dt className="font-medium text-slate-700">Confirmation</dt><dd className="mt-1 text-slate-500">Sends “{reviewEmail.confirmation_subject || "Registration confirmation"}” after a completed registration.</dd></div>
          </dl>
        </div>
      </div>
    );
  };

  const renderDraftRecovery = () => (
    <ProgramDraftRecovery conflict={draftConflict} recovered={recoveredDraft} serverLoadError={serverDraftLoadError} onRetryServer={retryServerDraft} onUseLocal={useLocalDraft} onUseServer={useServerDraft} onStartOver={discardLocalDraft} />
  );

  const renderStepErrorSummary = () => stepIssues.length > 0 ? (
    <div ref={errorSummaryRef} tabIndex={-1} role="alert" aria-labelledby="program-step-errors-title" className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900 outline-none focus-visible:ring-2 focus-visible:ring-red-700">
      <p id="program-step-errors-title" className="font-semibold">Fix {stepIssues.length === 1 ? "this field" : "these fields"} before continuing</p>
      <ul className="mt-2 list-disc space-y-1 pl-5">
        {stepIssues.map((issue) => <li key={`${issue.field || "step"}-${issue.label}`}>{issue.field ? <button type="button" className="text-left underline underline-offset-2" onClick={() => document.querySelector(`[data-program-field="${issue.field}"]`)?.focus()}>{issue.label}</button> : issue.label}</li>)}
      </ul>
    </div>
  ) : null;

  const renderStepContent = () => {
    // Get the current organization's subscription tier
    const currentOrg = organizations?.find(org => org.org_id === programDetails.org_id) || organizations?.[0];
    const subscriptionTier = currentOrg?.subscription_tier || 'free';
    const isFreeTier = subscriptionTier === 'free' || subscriptionTier === 'starter';
    
    // Filter program types based on subscription tier
    const availableTypes = PROGRAM_TYPES.filter(type => 
      type.tiers.includes(subscriptionTier)
    );
    const lockedTypes = PROGRAM_TYPES.filter(type => 
      !type.tiers.includes(subscriptionTier)
    );

    // For league programs, use simplified steps:
    // 1: Program Type, 2: League Details, 3: Registration Type, 4: Divisions, 
    // 5: Registration setup, 6: Payment Plans, 7: Email Confirmation
    if (isLeague) {
      if (currentStep === 2) return renderLeagueDetails();
      if (currentStep === 3) return renderLeagueRegistrationType();
      if (currentStep === 4) return renderDivisionsStep();
      if (currentStep === 5) return <div className="space-y-10">{renderDocumentsStep()}{renderAddonsStep()}</div>;
      if (currentStep === 6) return renderPaymentPlansStep();
      if (currentStep === 7) return renderEmailStep();
      if (currentStep === 8) return renderReviewStep();
    }

    // For tournament programs, use simplified steps:
    // For non-league programs (recreational, competitive, camps), use base steps
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">What type of program are you creating?</h3>
              <p className="text-slate-500">Choose the format that best fits your program&apos;s goals</p>
            </div>
            
            {/* Available Program Types */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {availableTypes.map((type) => (
                <div
                  key={type.id}
                  onClick={() => {
                    setProgramDetails({ 
                      ...programDetails, 
                      program_type: type.id,
                      // Set defaults based on type
                      tryouts_enabled: type.id === "competitive",
                      groups_enabled: type.id === "camp",
                      friend_requests_enabled: type.id === "recreational",
                      auto_team_building: type.id === "recreational",
                    });
                  }}
                  className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all hover:shadow-md ${
                    programDetails.program_type === type.id
                      ? 'border-slate-900 bg-slate-50 shadow-md'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                  data-testid={`program-type-${type.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-3xl ${
                      programDetails.program_type === type.id ? 'bg-slate-900 text-white' : 'bg-slate-100'
                    }`}>
                      {type.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-lg text-slate-900">{type.name}</span>
                        {programDetails.program_type === type.id && (
                          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{type.description}</p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {type.features.map((feature, idx) => (
                          <span key={idx} className={`text-xs px-2.5 py-1 rounded-full ${
                            programDetails.program_type === type.id 
                              ? 'bg-slate-200 text-slate-700' 
                              : 'bg-slate-100 text-slate-500'
                          }`}>
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Locked Program Types (for free tier) */}
            {lockedTypes.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Lock className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-medium text-slate-500">Upgrade to unlock more program types</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-60">
                  {lockedTypes.map((type) => (
                    <div
                      key={type.id}
                      className="relative p-6 rounded-2xl border-2 border-slate-200 bg-slate-50 cursor-not-allowed"
                      data-testid={`program-type-${type.id}-locked`}
                    >
                      <div className="absolute top-3 right-3">
                        <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                          <Lock className="w-3 h-3" />
                          <span>Pro</span>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl bg-slate-200">
                          {type.icon}
                        </div>
                        <div className="flex-1">
                          <span className="font-semibold text-lg text-slate-500">{type.name}</span>
                          <p className="text-sm text-slate-400 mt-1">{type.description}</p>
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            {type.features.map((feature, idx) => (
                              <span key={idx} className="text-xs px-2.5 py-1 rounded-full bg-slate-200 text-slate-400">
                                {feature}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Selected type summary */}
            {programDetails.program_type && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Check className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-blue-900">
                      {PROGRAM_TYPES.find(t => t.id === programDetails.program_type)?.name} Program Selected
                    </p>
                    <p className="text-sm text-blue-600">
                      Click Next to configure your program details
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-black">Basics and schedule</h3>
              <p className="mt-1 text-sm text-gray-500">Set the public identity, registration window, dates, location, and schedule families will see.</p>
            </div>
            
            {user?.role === "super_admin" && (
              <div>
                <Label>Organization *</Label>
                <Select 
                  value={programDetails.org_id} 
                  onValueChange={(value) => { setProgramDetails({ ...programDetails, org_id: value }); clearFieldIssue("org_id"); }}
                >
                  <SelectTrigger className="mt-1 border-gray-300" {...fieldErrorProps("org_id")}>
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations?.map(org => (
                      <SelectItem key={org.org_id} value={org.org_id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {renderFieldError("org_id")}
              </div>
            )}

            {/* Show selected program type badge */}
            <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-xl">
              <span className="text-xl">{PROGRAM_TYPES.find(t => t.id === programDetails.program_type)?.icon}</span>
              <span className="font-medium text-slate-700">
                {PROGRAM_TYPES.find(t => t.id === programDetails.program_type)?.name} Program
              </span>
              <button 
                onClick={() => setCurrentStep(1)} 
                className="ml-auto text-xs text-blue-600 hover:text-blue-700"
              >
                Change
              </button>
            </div>

            <div className={mode === "page" ? "athletic-basics-grid" : "grid grid-cols-1 gap-4 sm:grid-cols-2"}>
              <div className={mode === "page" ? "" : "sm:col-span-2"}>
                <Label>Program Name *</Label>
                <Input
                  {...fieldErrorProps("name")}
                  className="mt-1 border-gray-300"
                  placeholder="e.g., Youth Soccer League"
                  value={programDetails.name}
                  onChange={(e) => { setProgramDetails({ ...programDetails, name: e.target.value }); clearFieldIssue("name"); }}
                />
                {renderFieldError("name")}
              </div>

              <div className={mode === "page" ? "" : "sm:col-span-2"}>
                <Label>Sport *</Label>
                <Select 
                  value={programDetails.sport} 
                  onValueChange={(value) => { setProgramDetails({ ...programDetails, sport: value }); clearFieldIssue("sport"); }}
                >
                  <SelectTrigger className="mt-1 border-gray-300" {...fieldErrorProps("sport")}>
                    <SelectValue placeholder="Select sport" />
                  </SelectTrigger>
                  <SelectContent>
                    {sports.map(sport => (
                      <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {renderFieldError("sport")}
              </div>

              <div>
                <Label>Start Date</Label>
                <Input
                  {...fieldErrorProps("start_date")}
                  type="date"
                  className="mt-1 border-gray-300"
                  value={programDetails.start_date}
                  onChange={(e) => { setProgramDetails({ ...programDetails, start_date: e.target.value }); clearFieldIssue("start_date"); }}
                />
                {renderFieldError("start_date")}
              </div>

              <div>
                <Label>End Date</Label>
                <Input
                  {...fieldErrorProps("end_date")}
                  type="date"
                  className="mt-1 border-gray-300"
                  value={programDetails.end_date}
                  onChange={(e) => { setProgramDetails({ ...programDetails, end_date: e.target.value }); clearFieldIssue("end_date"); }}
                />
                {renderFieldError("end_date")}
              </div>

              <div>
                <Label>Registration Opens</Label>
                <Input
                  {...fieldErrorProps("registration_open_date")}
                  type="date"
                  className="mt-1 border-gray-300"
                  value={programDetails.registration_open_date}
                  onChange={(e) => { setProgramDetails({ ...programDetails, registration_open_date: e.target.value }); clearFieldIssue("registration_open_date"); }}
                />
                {renderFieldError("registration_open_date")}
              </div>

              <div>
                <Label>Registration Deadline</Label>
                <Input
                  {...fieldErrorProps("registration_deadline")}
                  type="date"
                  className="mt-1 border-gray-300"
                  value={programDetails.registration_deadline}
                  onChange={(e) => { setProgramDetails({ ...programDetails, registration_deadline: e.target.value }); clearFieldIssue("registration_deadline"); }}
                />
                {renderFieldError("registration_deadline")}
              </div>

              <div className="sm:col-span-2">
                <Label>Location</Label>
                <p className="text-xs text-gray-500 mb-1">Search for a venue, park, or address</p>
                <div className="mt-1">
                  <AddressAutocomplete
                    value={programDetails.venue_address || programDetails.location || ''}
                    onChange={(value) => setProgramDetails({ 
                      ...programDetails, 
                      venue_address: value,
                      // Keep location in sync for display purposes
                      location: value.split(',')[0].trim()
                    })}
                    onPlaceSelect={(place) => {
                      setProgramDetails({ 
                        ...programDetails, 
                        location: place.name || place.address.split(',')[0].trim(),
                        venue_address: place.address
                      });
                    }}
                    placeholder="Start typing venue name or address..."
                    className="border-gray-300"
                    searchType="all"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <Label>Schedule</Label>
                <Input
                  className="mt-1 border-gray-300"
                  placeholder="e.g., Saturdays 9:00 AM - 12:00 PM"
                  value={programDetails.schedule}
                  onChange={(e) => setProgramDetails({ ...programDetails, schedule: e.target.value })}
                />
              </div>

              <div className="sm:col-span-2">
                <Label>Description</Label>
                <div className="mt-1">
                  <RichTextEditor
                    value={programDetails.description}
                    onChange={(value) => setProgramDetails({ ...programDetails, description: value })}
                    placeholder="Describe your program..."
                  />
                </div>
              </div>
            </div>

            {/* Program Branding */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="font-medium text-black mb-4">Registration Page Branding</h4>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Logo Upload */}
                <div>
                  <Label className="text-sm">Program Logo</Label>
                  <p className="text-xs text-gray-500 mb-2">Displayed on the registration page header</p>
                  <div className="mt-1">
                    {programDetails.logo_url ? (
                      <div className="relative w-32 h-32 rounded-2xl overflow-hidden border border-gray-300 bg-white">
                        <img 
                          src={programDetails.logo_url} 
                          alt="Program Logo" 
                          className="w-full h-full object-contain p-2"
                        />
                        <button
                          type="button"
                          onClick={() => setProgramDetails({ ...programDetails, logo_url: '' })}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-32 h-32 border-2 border-dashed border-gray-300 rounded-2xl cursor-pointer hover:bg-gray-50 transition-colors">
                        <Image className="w-8 h-8 text-gray-400 mb-1" />
                        <span className="text-xs text-gray-500">Add Logo</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleProgramImageUpload('logo_url', e)}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Splash Image Upload */}
                <div>
                  <Label className="text-sm">Background Splash Image</Label>
                  <p className="text-xs text-gray-500 mb-2">Hero image shown on the registration page (16:9 aspect ratio)</p>
                  <div className="mt-1">
                    {programDetails.splash_image_url ? (
                      <div className="relative w-full h-32 rounded-2xl overflow-hidden border border-gray-300 group">
                        <img 
                          src={programDetails.splash_image_url} 
                          alt="Splash Image" 
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleProgramImageUpload('splash_image_url', e)}
                            />
                            <span className="bg-white text-gray-800 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 hover:bg-gray-100">
                              <Crop className="w-4 h-4" />
                              Replace & Crop
                            </span>
                          </label>
                        </div>
                        <button
                          type="button"
                          onClick={() => setProgramDetails({ ...programDetails, splash_image_url: '' })}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-2xl cursor-pointer hover:bg-gray-50 transition-colors">
                        <Image className="w-8 h-8 text-gray-400 mb-1" />
                        <span className="text-xs text-gray-500">Add Splash Image</span>
                        <span className="text-xs text-gray-400">Recommended: 1920x1080px (16:9)</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleProgramImageUpload('splash_image_url', e)}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* Custom URL Slug */}
              <div className="pt-4 border-t border-gray-200">
                <Label className="text-black font-medium">Registration URL (Optional)</Label>
                <p className="text-xs text-gray-500 mb-2">
                  Create a custom short link for sharing. Only lowercase letters, numbers, and hyphens.
                </p>
                {(() => {
                  const selectedOrg = organizations?.find(org => org.org_id === programDetails.org_id);
                  const baseUrl = selectedOrg?.subdomain 
                    ? `https://${selectedOrg.subdomain}.sporthouse.ai`
                    : window.location.origin;
                  return (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">{baseUrl}/r/</span>
                        <Input
                          className="flex-1 border-gray-300"
                          placeholder="spring-soccer-2025"
                          value={programDetails.slug}
                          onChange={(e) => {
                            const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                            setProgramDetails({ ...programDetails, slug: value });
                          }}
                          data-testid="program-slug-input"
                        />
                      </div>
                      {programDetails.slug && (
                        <p className="text-xs text-green-600 mt-1">
                          Your registration link: {baseUrl}/r/{programDetails.slug}
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Type-Specific Configuration */}
            {programDetails.program_type === "competitive" && (
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-2xl" data-testid="tryouts-config">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xl">🏆</span>
                  <h4 className="font-semibold text-amber-800">Competitive Program - Tryouts Configuration</h4>
                </div>
                
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="tryouts_enabled"
                    checked={programDetails.tryouts_enabled}
                    onChange={(e) => setProgramDetails({ ...programDetails, tryouts_enabled: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded"
                  />
                  <Label htmlFor="tryouts_enabled" className="text-amber-800 cursor-pointer">
                    Enable Tryouts
                  </Label>
                </div>

                {programDetails.tryouts_enabled && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 pl-7">
                    <div>
                      <Label className="text-amber-700">Tryout Date</Label>
                      <Input
                        type="date"
                        className="mt-1 border-amber-300 bg-white"
                        value={programDetails.tryouts_date}
                        onChange={(e) => setProgramDetails({ ...programDetails, tryouts_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-amber-700">Tryout Location</Label>
                      <div className="mt-1">
                        <AddressAutocomplete
                          value={programDetails.tryouts_location}
                          onChange={(address) => setProgramDetails({ ...programDetails, tryouts_location: address })}
                          placeholder="Start typing to search..."
                          className="border-amber-300 bg-white"
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-amber-700">Tryout Details</Label>
                      <Textarea
                        className="mt-1 border-amber-300 bg-white"
                        placeholder="What to bring, what to expect..."
                        rows={2}
                        value={programDetails.tryouts_details}
                        onChange={(e) => setProgramDetails({ ...programDetails, tryouts_details: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {normalizeProgramType(programDetails.program_type) === "camp" && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-2xl" data-testid="camps-config">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xl">⛺</span>
                  <h4 className="font-semibold text-green-800">Camp Configuration</h4>
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="groups_enabled"
                    checked={programDetails.groups_enabled}
                    onChange={(e) => setProgramDetails({ ...programDetails, groups_enabled: e.target.checked })}
                    className="w-4 h-4 text-green-600 rounded"
                  />
                  <div>
                    <Label htmlFor="groups_enabled" className="text-green-800 cursor-pointer">
                      Use Groups Instead of Teams
                    </Label>
                    <p className="text-xs text-green-600">
                      Participants will be organized into activity groups rather than competitive teams
                    </p>
                  </div>
                </div>
              </div>
            )}

            {programDetails.program_type === "recreational" && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-2xl" data-testid="recreational-config">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xl">🎮</span>
                  <h4 className="font-semibold text-blue-800">Recreational Program Features</h4>
                </div>
                
                <div className="space-y-4">
                  {/* Friend Requests Toggle */}
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="friend_requests"
                      checked={programDetails.friend_requests_enabled}
                      onChange={(e) => setProgramDetails({ ...programDetails, friend_requests_enabled: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <div>
                      <Label htmlFor="friend_requests" className="text-blue-800 cursor-pointer">
                        Allow Friend Requests
                      </Label>
                      <p className="text-xs text-blue-600">
                        Let parents request to be on the same team as friends during registration
                      </p>
                    </div>
                  </div>

                  {/* Friend Requests Configuration - Expanded Section */}
                  {programDetails.friend_requests_enabled && (
                    <div className="ml-7 p-4 bg-white rounded-xl border border-blue-200 space-y-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-4 h-4 text-blue-600" />
                        <span className="font-medium text-blue-800 text-sm">Friend Request Settings</span>
                      </div>

                      {/* Max Friend Requests */}
                      <div>
                        <Label className="text-blue-700 text-sm">Maximum Friend Requests Per Player</Label>
                        <p className="text-xs text-blue-500 mb-2">How many friends can each player request to be paired with?</p>
                        <Select 
                          value={programDetails.max_friend_requests?.toString() || "2"} 
                          onValueChange={(value) => setProgramDetails({ ...programDetails, max_friend_requests: parseInt(value) })}
                        >
                          <SelectTrigger className="w-full border-blue-200 bg-white">
                            <SelectValue placeholder="Select limit" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 friend</SelectItem>
                            <SelectItem value="2">2 friends</SelectItem>
                            <SelectItem value="3">3 friends</SelectItem>
                            <SelectItem value="5">5 friends</SelectItem>
                            <SelectItem value="0">Unlimited</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Request Type */}
                      <div>
                        <Label className="text-blue-700 text-sm">Request Matching</Label>
                        <p className="text-xs text-blue-500 mb-2">How should friend requests be matched?</p>
                        <div className="space-y-2">
                          <label className="flex items-start gap-3 p-3 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                            <input
                              type="radio"
                              name="friend_request_type"
                              value="mutual"
                              checked={programDetails.friend_request_type === "mutual" || !programDetails.friend_request_type}
                              onChange={() => setProgramDetails({ ...programDetails, friend_request_type: "mutual" })}
                              className="mt-0.5 text-blue-600"
                            />
                            <div>
                              <span className="font-medium text-blue-800 text-sm">Mutual Requests Required</span>
                              <p className="text-xs text-blue-500">Both players must request each other to be paired together</p>
                            </div>
                          </label>
                          <label className="flex items-start gap-3 p-3 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                            <input
                              type="radio"
                              name="friend_request_type"
                              value="one_way"
                              checked={programDetails.friend_request_type === "one_way"}
                              onChange={() => setProgramDetails({ ...programDetails, friend_request_type: "one_way" })}
                              className="mt-0.5 text-blue-600"
                            />
                            <div>
                              <span className="font-medium text-blue-800 text-sm">One-Way Requests</span>
                              <p className="text-xs text-blue-500">Only one player needs to request the other</p>
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* Friend Request Input Method */}
                      <div>
                        <Label className="text-blue-700 text-sm">How Will Parents Enter Friend Requests?</Label>
                        <p className="text-xs text-blue-500 mb-2">Choose how parents identify friends during registration</p>
                        <div className="space-y-2">
                          <label className="flex items-start gap-3 p-3 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                            <input
                              type="radio"
                              name="friend_input_method"
                              value="name"
                              checked={programDetails.friend_input_method === "name" || !programDetails.friend_input_method}
                              onChange={() => setProgramDetails({ ...programDetails, friend_input_method: "name" })}
                              className="mt-0.5 text-blue-600"
                            />
                            <div>
                              <span className="font-medium text-blue-800 text-sm">By Name</span>
                              <p className="text-xs text-blue-500">Parents type their friend&apos;s name (you&apos;ll match manually or by system)</p>
                            </div>
                          </label>
                          <label className="flex items-start gap-3 p-3 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                            <input
                              type="radio"
                              name="friend_input_method"
                              value="code"
                              checked={programDetails.friend_input_method === "code"}
                              onChange={() => setProgramDetails({ ...programDetails, friend_input_method: "code" })}
                              className="mt-0.5 text-blue-600"
                            />
                            <div>
                              <span className="font-medium text-blue-800 text-sm">By Friend Code</span>
                              <p className="text-xs text-blue-500">Each player gets a unique code to share with friends for exact matching</p>
                            </div>
                          </label>
                          <label className="flex items-start gap-3 p-3 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                            <input
                              type="radio"
                              name="friend_input_method"
                              value="dropdown"
                              checked={programDetails.friend_input_method === "dropdown"}
                              onChange={() => setProgramDetails({ ...programDetails, friend_input_method: "dropdown" })}
                              className="mt-0.5 text-blue-600"
                            />
                            <div>
                              <span className="font-medium text-blue-800 text-sm">From Registered Players</span>
                              <p className="text-xs text-blue-500">Parents select from a list of already registered players</p>
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* Priority Setting */}
                      <div className="flex items-center gap-3 pt-2 border-t border-blue-100">
                        <input
                          type="checkbox"
                          id="friend_request_priority"
                          checked={programDetails.friend_request_priority}
                          onChange={(e) => setProgramDetails({ ...programDetails, friend_request_priority: e.target.checked })}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <div>
                          <Label htmlFor="friend_request_priority" className="text-blue-700 text-sm cursor-pointer">
                            Prioritize Friend Requests Over Team Balance
                          </Label>
                          <p className="text-xs text-blue-500">
                            When enabled, the system will honor friend requests even if it creates slightly unbalanced teams
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Auto Team Building */}
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="auto_team_building"
                      checked={programDetails.auto_team_building}
                      onChange={(e) => setProgramDetails({ ...programDetails, auto_team_building: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <div>
                      <Label htmlFor="auto_team_building" className="text-blue-800 cursor-pointer">
                        Automatic Team Building
                      </Label>
                      <p className="text-xs text-blue-600">
                        System will automatically balance teams based on age, skill level, and friend requests
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {programDetails.program_type === "tournament" && (
              <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-2xl" data-testid="tournament-config">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="h-5 w-5 text-purple-600" />
                  <h4 className="font-semibold text-purple-800">Tournament Configuration</h4>
                </div>
                <p className="text-sm text-purple-600 mb-4">
                  A full tournament will be created with bracket management, team seeding, and live scoring.
                </p>
                
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-purple-700">Tournament Format</Label>
                    <Select 
                      value={programDetails.tournament_format} 
                      onValueChange={(value) => setProgramDetails({ ...programDetails, tournament_format: value })}
                    >
                      <SelectTrigger className="mt-1 border-purple-300 bg-white">
                        <SelectValue placeholder="Select format" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single_elimination">Single Elimination</SelectItem>
                        <SelectItem value="double_elimination">Double Elimination</SelectItem>
                        <SelectItem value="round_robin">Round Robin</SelectItem>
                        <SelectItem value="pool_play">Pool Play + Bracket</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-purple-700">Number of Teams</Label>
                    <Select 
                      value={programDetails.tournament_teams_count} 
                      onValueChange={(value) => setProgramDetails({ ...programDetails, tournament_teams_count: value })}
                    >
                      <SelectTrigger className="mt-1 border-purple-300 bg-white">
                        <SelectValue placeholder="Select teams" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4">4 Teams</SelectItem>
                        <SelectItem value="8">8 Teams</SelectItem>
                        <SelectItem value="12">12 Teams</SelectItem>
                        <SelectItem value="16">16 Teams</SelectItem>
                        <SelectItem value="24">24 Teams</SelectItem>
                        <SelectItem value="32">32 Teams</SelectItem>
                        <SelectItem value="64">64 Teams</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-purple-700">Game Duration (minutes)</Label>
                    <Select 
                      value={programDetails.tournament_game_duration || "60"} 
                      onValueChange={(value) => setProgramDetails({ ...programDetails, tournament_game_duration: value })}
                    >
                      <SelectTrigger className="mt-1 border-purple-300 bg-white">
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="40">40 minutes</SelectItem>
                        <SelectItem value="50">50 minutes</SelectItem>
                        <SelectItem value="60">60 minutes</SelectItem>
                        <SelectItem value="70">70 minutes</SelectItem>
                        <SelectItem value="80">80 minutes</SelectItem>
                        <SelectItem value="90">90 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-purple-700">Team Entry Fee</Label>
                    <Input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={programDetails.tournament_entry_fee || ""}
                      onChange={(e) => setProgramDetails({ ...programDetails, tournament_entry_fee: e.target.value })}
                      className="mt-1 border-purple-300 bg-white"
                    />
                  </div>
                </div>
                
                {/* Pool Play Settings */}
                {programDetails.tournament_format === "pool_play" && (
                  <div className="mt-4 pt-4 border-t border-purple-200">
                    <h5 className="font-medium text-purple-700 mb-3">Pool Play Settings</h5>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <Label className="text-purple-700 text-sm">Number of Pools</Label>
                        <Select 
                          value={programDetails.tournament_pools_count || "4"} 
                          onValueChange={(value) => setProgramDetails({ ...programDetails, tournament_pools_count: value })}
                        >
                          <SelectTrigger className="mt-1 border-purple-300 bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2">2 Pools</SelectItem>
                            <SelectItem value="3">3 Pools</SelectItem>
                            <SelectItem value="4">4 Pools</SelectItem>
                            <SelectItem value="6">6 Pools</SelectItem>
                            <SelectItem value="8">8 Pools</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-purple-700 text-sm">Teams Per Pool</Label>
                        <Select 
                          value={programDetails.tournament_teams_per_pool || "4"} 
                          onValueChange={(value) => setProgramDetails({ ...programDetails, tournament_teams_per_pool: value })}
                        >
                          <SelectTrigger className="mt-1 border-purple-300 bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="3">3 Teams</SelectItem>
                            <SelectItem value="4">4 Teams</SelectItem>
                            <SelectItem value="5">5 Teams</SelectItem>
                            <SelectItem value="6">6 Teams</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Bracket Options */}
                {["single_elimination", "double_elimination", "pool_play"].includes(programDetails.tournament_format) && (
                  <div className="mt-4 pt-4 border-t border-purple-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-purple-700">Third Place Game</p>
                        <p className="text-xs text-purple-500">Play a game for 3rd and 4th place</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={programDetails.tournament_third_place_game !== false}
                        onChange={(e) => setProgramDetails({ ...programDetails, tournament_third_place_game: e.target.checked })}
                        className="h-4 w-4 text-purple-600 rounded"
                      />
                    </div>
                  </div>
                )}
                
                <div className="mt-4 p-3 bg-purple-100 rounded-lg">
                  <p className="text-xs text-purple-700">
                    <strong>Note:</strong> A full tournament bracket will be created when this program is saved. 
                    You can manage teams, seeding, and live scoring from the Program Dashboard.
                  </p>
                </div>
              </div>
            )}

            {programDetails.program_type === "league" && (
              <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded-2xl" data-testid="league-config">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">📊</span>
                  <h4 className="font-semibold text-indigo-800">League Configuration</h4>
                </div>
                <p className="text-sm text-indigo-600">
                  League programs include automatic standings tracking, scheduled game days, and playoff brackets. 
                  Teams will be organized by division with regular season games.
                </p>
              </div>
            )}

            {/* Registration Display Options */}
            <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-2xl" data-testid="registration-display-options">
              <div className="flex items-center gap-2 mb-4">
                <Settings className="w-5 h-5 text-slate-600" />
                <h4 className="font-semibold text-slate-800">Registration Display Options</h4>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Choose what optional sections to show during the registration process.
              </p>
              
              <div className="space-y-3">
                <label className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-slate-500" />
                    <div>
                      <span className="font-medium text-slate-700">Add-ons</span>
                      <p className="text-xs text-slate-500">Optional extras like jerseys, equipment, etc.</p>
                    </div>
                  </div>
                  <Switch
                    checked={programDetails.show_addons_in_registration}
                    onCheckedChange={(checked) => setProgramDetails({ ...programDetails, show_addons_in_registration: checked })}
                  />
                </label>
                
                <label className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <ShoppingBag className="w-5 h-5 text-slate-500" />
                    <div>
                      <span className="font-medium text-slate-700">Team Gear / Merchandise</span>
                      <p className="text-xs text-slate-500">Printful-integrated team merchandise store</p>
                    </div>
                  </div>
                  <Switch
                    checked={programDetails.show_merchandise_in_registration}
                    onCheckedChange={(checked) => setProgramDetails({ ...programDetails, show_merchandise_in_registration: checked })}
                  />
                </label>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-black">Audience and capacity</h3>
                <p className="text-sm text-gray-500">Define who can enroll, available spots, and the price for each eligible group.</p>
              </div>
              <Button onClick={addDivision} variant="outline" size="sm" className="border-gray-300">
                <Plus className="w-4 h-4 mr-1" /> Add Division
              </Button>
            </div>

            <div className="space-y-4">
              {divisions.map((division, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-2xl bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-black">Division {index + 1}</span>
                    {divisions.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeDivision(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  {/* Row 1: Name and Gender */}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 mb-3">
                    <div>
                      <Label className="text-xs">Division Name *</Label>
                      <Input
                        {...fieldErrorProps(`divisions.${index}.name`)}
                        className="mt-1 border-gray-300 text-sm"
                        placeholder="e.g., U8, Beginners, Varsity"
                        value={division.name}
                        onChange={(e) => updateDivision(index, "name", e.target.value)}
                      />
                      {renderFieldError(`divisions.${index}.name`)}
                    </div>
                    <div>
                      <Label className="text-xs">Gender</Label>
                      <Select 
                        value={division.gender} 
                        onValueChange={(value) => updateDivision(index, "gender", value)}
                      >
                        <SelectTrigger className="mt-1 border-gray-300 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="coed">Co-ed</SelectItem>
                          <SelectItem value="boys">Boys Only</SelectItem>
                          <SelectItem value="girls">Girls Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Age/Birth Date Section */}
                  <div className="p-3 bg-white border border-gray-200 rounded-xl mb-3">
                    <Label className="text-xs font-medium text-gray-700 mb-2 block">
                      Eligible Birth Dates *
                    </Label>
                    
                    {/* Quick Select Options */}
                    <div className="flex gap-2 mb-3">
                      <Select
                        value=""
                        onValueChange={(value) => {
                          const option = birthYearOptions.find(o => o.year.toString() === value);
                          if (option) {
                            updateDivision(index, "birth_date_start", option.start);
                            updateDivision(index, "birth_date_end", option.end);
                          }
                        }}
                      >
                        <SelectTrigger className="flex-1 border-gray-300 text-sm h-9">
                          <SelectValue placeholder="Quick: Birth Year" />
                        </SelectTrigger>
                        <SelectContent>
                          {birthYearOptions.map(option => (
                            <SelectItem key={option.year} value={option.year.toString()}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <Select
                        value=""
                        onValueChange={(value) => {
                          const option = schoolYearOptions.find(o => o.start === value);
                          if (option) {
                            updateDivision(index, "birth_date_start", option.start);
                            updateDivision(index, "birth_date_end", option.end);
                          }
                        }}
                      >
                        <SelectTrigger className="flex-1 border-gray-300 text-sm h-9">
                          <SelectValue placeholder="Quick: School Year" />
                        </SelectTrigger>
                        <SelectContent>
                          {schoolYearOptions.map(option => (
                            <SelectItem key={option.start} value={option.start}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Manual Date Inputs */}
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <Label className="text-xs text-gray-500">Oldest (Born On/After)</Label>
                        <Input
                          {...fieldErrorProps(`divisions.${index}.birth_date_start`)}
                          type="date"
                          className="mt-1 border-gray-300 text-sm"
                          value={division.birth_date_start}
                          onChange={(e) => updateDivision(index, "birth_date_start", e.target.value)}
                        />
                        {renderFieldError(`divisions.${index}.birth_date_start`)}
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Youngest (Born On/Before)</Label>
                        <Input
                          {...fieldErrorProps(`divisions.${index}.birth_date_end`)}
                          type="date"
                          className="mt-1 border-gray-300 text-sm"
                          value={division.birth_date_end}
                          onChange={(e) => updateDivision(index, "birth_date_end", e.target.value)}
                        />
                        {renderFieldError(`divisions.${index}.birth_date_end`)}
                      </div>
                    </div>

                    {/* Display calculated age range */}
                    {division.birth_date_start && division.birth_date_end && (
                      <div className="mt-2 px-2 py-1 bg-blue-50 rounded text-xs text-blue-700">
                        {formatDateRange(division.birth_date_start, division.birth_date_end)}
                        {' '}
                        <span className="text-blue-500">
                          (Ages {calculateAgeRange(division.birth_date_start, division.birth_date_end)} as of today)
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Row 3: Max Players and Price */}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <Label className="text-xs">Max Players</Label>
                      <Input
                        {...fieldErrorProps(`divisions.${index}.max_players`)}
                        type="number"
                        className="mt-1 border-gray-300 text-sm"
                        placeholder="30"
                        value={division.max_players}
                        onChange={(e) => updateDivision(index, "max_players", e.target.value)}
                      />
                      {renderFieldError(`divisions.${index}.max_players`)}
                    </div>
                    <div>
                      <Label className="text-xs">Price ($) *</Label>
                      <Input
                        {...fieldErrorProps(`divisions.${index}.price`)}
                        type="number"
                        step="0.01"
                        className="mt-1 border-gray-300 text-sm"
                        placeholder="150.00"
                        value={division.price}
                        onChange={(e) => updateDivision(index, "price", e.target.value)}
                      />
                      {renderFieldError(`divisions.${index}.price`)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-black">Pricing and payment options</h3>
                <p className="text-sm text-gray-500">Offer full payment, installments, and early-registration savings.</p>
              </div>
              <Button onClick={addPaymentPlan} variant="outline" size="sm" className="border-gray-300">
                <Plus className="w-4 h-4 mr-1" /> Add Plan
              </Button>
            </div>

            {/* Early Bird Discount */}
            <div className="p-4 border border-gray-200 rounded-2xl bg-blue-50">
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  id="earlybird"
                  checked={earlyBirdDiscount.enabled}
                  onChange={(e) => setEarlyBirdDiscount({ ...earlyBirdDiscount, enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <Label htmlFor="earlybird" className="font-medium cursor-pointer">Enable Early Bird Discount</Label>
              </div>
              {earlyBirdDiscount.enabled && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs">Discount (%)</Label>
                    <Input
                      type="number"
                      className="mt-1 border-gray-300 text-sm bg-white"
                      placeholder="10"
                      value={earlyBirdDiscount.discount_percent}
                      onChange={(e) => setEarlyBirdDiscount({ ...earlyBirdDiscount, discount_percent: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Deadline</Label>
                    <Input
                      type="date"
                      className="mt-1 border-gray-300 text-sm bg-white"
                      value={earlyBirdDiscount.deadline}
                      onChange={(e) => setEarlyBirdDiscount({ ...earlyBirdDiscount, deadline: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Payment Plans */}
            <div className="space-y-3">
              {/* Info box about division pricing */}
              <div className="p-3 bg-gray-100 rounded-lg text-sm text-gray-600">
                <strong>Note:</strong> Payment plans apply to the prices set in each division. Total cost comes from the selected division.
              </div>
              
              {paymentPlans.map((plan, index) => (
                <div key={index} className={`p-4 border rounded-2xl ${plan.is_required ? 'border-black bg-gray-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="defaultPlan"
                        checked={plan.is_default}
                        onChange={() => updatePaymentPlan(index, "is_default", true)}
                        className="w-4 h-4"
                      />
                      <span className="text-xs text-gray-500">Default</span>
                      {plan.is_required && (
                        <span className="text-xs bg-black text-white px-2 py-0.5 rounded-full">Required</span>
                      )}
                    </div>
                    {!plan.is_required && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removePaymentPlan(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div>
                      <Label className="text-xs">Plan Name *</Label>
                      <Input
                        {...fieldErrorProps(`paymentPlans.${index}.name`)}
                        className="mt-1 border-gray-300 text-sm"
                        placeholder="e.g., 3 Monthly Payments"
                        value={plan.name}
                        onChange={(e) => updatePaymentPlan(index, "name", e.target.value)}
                        disabled={plan.is_required}
                      />
                      {renderFieldError(`paymentPlans.${index}.name`)}
                    </div>
                    <div>
                      <Label className="text-xs"># of Payments *</Label>
                      <Select 
                        value={String(plan.num_payments || 1)} 
                        onValueChange={(value) => updatePaymentPlan(index, "num_payments", parseInt(value))}
                        disabled={plan.is_required}
                      >
                        <SelectTrigger className="mt-1 border-gray-300 text-sm" {...fieldErrorProps(`paymentPlans.${index}.num_payments`)}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 (Full)</SelectItem>
                          <SelectItem value="2">2 Payments</SelectItem>
                          <SelectItem value="3">3 Payments</SelectItem>
                          <SelectItem value="4">4 Payments</SelectItem>
                          <SelectItem value="5">5 Payments</SelectItem>
                          <SelectItem value="6">6 Payments</SelectItem>
                        </SelectContent>
                      </Select>
                      {renderFieldError(`paymentPlans.${index}.num_payments`)}
                    </div>
                    <div>
                      <Label className="text-xs">Deposit ($) {plan.num_payments > 1 ? '' : '(optional)'}</Label>
                      <Input
                        {...fieldErrorProps(`paymentPlans.${index}.deposit`)}
                        type="number"
                        step="0.01"
                        className="mt-1 border-gray-300 text-sm"
                        placeholder="50.00"
                        value={plan.deposit}
                        onChange={(e) => updatePaymentPlan(index, "deposit", e.target.value)}
                        disabled={plan.is_required}
                      />
                      {renderFieldError(`paymentPlans.${index}.deposit`)}
                      {plan.num_payments > 1 && (
                        <p className="text-xs text-gray-400 mt-1">Due at registration</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-10">
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-black">Forms and waivers</h3>
                <p className="text-sm text-gray-500">Add the policies, acknowledgements, and documents families must review.</p>
              </div>
              <Button onClick={addDocument} variant="outline" size="sm" className="border-gray-300">
                <Plus className="w-4 h-4 mr-1" /> Add Document
              </Button>
            </div>

            {documents.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-gray-300 rounded-2xl">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No documents required</p>
                <Button onClick={addDocument} variant="outline" size="sm" className="mt-2">
                  Add Document
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {documents.map((doc, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-2xl bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={doc.required}
                          onChange={(e) => updateDocument(index, "required", e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <span className="text-xs text-gray-500">Required</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeDocument(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 mb-3">
                      <div className="sm:col-span-2">
                        <Label className="text-xs">Document Name</Label>
                        <Input
                          className="mt-1 border-gray-300 text-sm"
                          placeholder="e.g., Medical Release Form"
                          value={doc.name}
                          onChange={(e) => updateDocument(index, "name", e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Type</Label>
                        <Select 
                          value={doc.type} 
                          onValueChange={(value) => updateDocument(index, "type", value)}
                        >
                          <SelectTrigger className="mt-1 border-gray-300 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="waiver">Waiver</SelectItem>
                            <SelectItem value="form">Form</SelectItem>
                            <SelectItem value="upload">File Upload</SelectItem>
                            <SelectItem value="agreement">Agreement</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Document Content Area */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs">Document Content</Label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => generateDocumentWithAI(index, doc.type)}
                          disabled={aiLoading[`doc_${index}`] || !hasFeature(Features.AI_FEATURES)}
                          className="text-xs h-7 border-gray-300"
                          title={!hasFeature(Features.AI_FEATURES) ? "AI features require Plus or Premium plan" : ""}
                        >
                          {aiLoading[`doc_${index}`] ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : !hasFeature(Features.AI_FEATURES) ? (
                            <Lock className="w-3 h-3 mr-1 text-slate-400" />
                          ) : (
                            <Sparkles className="w-3 h-3 mr-1" />
                          )}
                          {!hasFeature(Features.AI_FEATURES) ? "AI (Upgrade)" : "Generate with AI"}
                        </Button>
                      </div>
                      <Textarea
                        className="mt-1 border-gray-300 text-sm font-mono bg-white"
                        placeholder="Paste your document content here or use AI to generate..."
                        rows={8}
                        value={doc.content}
                        onChange={(e) => updateDocument(index, "content", e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-black">Registration extras</h3>
                <p className="text-sm text-gray-500">Offer optional merchandise, equipment, or other purchasable items.</p>
              </div>
              <Button onClick={addAddon} variant="outline" size="sm" className="border-gray-300">
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            </div>

            {addons.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-gray-300 rounded-2xl">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No add-ons configured</p>
                <Button onClick={addAddon} variant="outline" size="sm" className="mt-2">
                  Add Item
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {addons.map((addon, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-2xl bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={addon.required}
                          onChange={(e) => updateAddon(index, "required", e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <span className="text-xs text-gray-500">Required</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeAddon(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                      {/* Photo Upload */}
                      <div className="row-span-3">
                        <Label className="text-xs">Photo</Label>
                        <div className="mt-1">
                          {addon.photo_url ? (
                            <div className="relative w-full aspect-square rounded-lg overflow-hidden border border-gray-300">
                              <img 
                                src={addon.photo_url} 
                                alt={addon.name || "Add-on"} 
                                className="w-full h-full object-cover"
                              />
                              <button
                                onClick={() => updateAddon(index, 'photo_url', '')}
                                className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <label className="flex flex-col items-center justify-center w-full aspect-square border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                              <Image className="w-8 h-8 text-gray-400 mb-1" />
                              <span className="text-xs text-gray-500">Add Photo</span>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handlePhotoUpload(index, e)}
                              />
                            </label>
                          )}
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs">Item Name</Label>
                        <Input
                          className="mt-1 border-gray-300 text-sm"
                          placeholder="e.g., Team Jersey"
                          value={addon.name}
                          onChange={(e) => updateAddon(index, "name", e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Price ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          className="mt-1 border-gray-300 text-sm"
                          placeholder="25.00"
                          value={addon.price}
                          onChange={(e) => updateAddon(index, "price", e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Max Qty</Label>
                        <Input
                          type="number"
                          className="mt-1 border-gray-300 text-sm"
                          placeholder="1"
                          value={addon.max_quantity}
                          onChange={(e) => updateAddon(index, "max_quantity", e.target.value)}
                        />
                      </div>
                      <div className="sm:col-span-2 xl:col-span-4">
                        <Label className="text-xs">Description</Label>
                        <Input
                          className="mt-1 border-gray-300 text-sm"
                          placeholder="Brief description"
                          value={addon.description}
                          onChange={(e) => updateAddon(index, "description", e.target.value)}
                        />
                      </div>
                      
                      {/* Size Options */}
                      <div className="sm:col-span-2">
                        <div className="flex items-center gap-2 mb-1">
                          <input
                            type="checkbox"
                            id={`size-${index}`}
                            checked={addon.has_sizes}
                            onChange={(e) => updateAddon(index, "has_sizes", e.target.checked)}
                            className="w-3 h-3 rounded border-gray-300"
                          />
                          <Label htmlFor={`size-${index}`} className="text-xs cursor-pointer">Size Options (optional)</Label>
                        </div>
                        {addon.has_sizes && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {sizeOptions.map(size => (
                              <button
                                key={size}
                                type="button"
                                onClick={() => {
                                  const sizes = addon.available_sizes || [];
                                  const updated = sizes.includes(size) 
                                    ? sizes.filter(s => s !== size)
                                    : [...sizes, size];
                                  updateAddon(index, "available_sizes", updated);
                                }}
                                className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                                  (addon.available_sizes || []).includes(size)
                                    ? 'bg-black text-white border-black'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                                }`}
                              >
                                {size}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Color Options */}
                      <div className="sm:col-span-2">
                        <div className="flex items-center gap-2 mb-1">
                          <input
                            type="checkbox"
                            id={`color-${index}`}
                            checked={addon.has_colors}
                            onChange={(e) => updateAddon(index, "has_colors", e.target.checked)}
                            className="w-3 h-3 rounded border-gray-300"
                          />
                          <Label htmlFor={`color-${index}`} className="text-xs cursor-pointer">Color Options (optional)</Label>
                        </div>
                        {addon.has_colors && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {colorOptions.map(color => (
                              <button
                                key={color}
                                type="button"
                                onClick={() => {
                                  const colors = addon.available_colors || [];
                                  const updated = colors.includes(color) 
                                    ? colors.filter(c => c !== color)
                                    : [...colors, color];
                                  updateAddon(index, "available_colors", updated);
                                }}
                                className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                                  (addon.available_colors || []).includes(color)
                                    ? 'bg-black text-white border-black'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                                }`}
                              >
                                {color}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-black">Registration communications</h3>
                <p className="text-sm text-gray-500">Customize the registration confirmation email families receive.</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={generateEmailWithAI}
                disabled={aiLoading.email || !hasFeature(Features.AI_FEATURES)}
                className="border-gray-300"
                title={!hasFeature(Features.AI_FEATURES) ? "AI features require Plus or Premium plan" : ""}
              >
                {aiLoading.email ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : !hasFeature(Features.AI_FEATURES) ? (
                  <Lock className="w-4 h-4 mr-1 text-slate-400" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-1" />
                )}
                {!hasFeature(Features.AI_FEATURES) ? "AI (Upgrade)" : "Generate with AI"}
              </Button>
            </div>

            <div>
              <Label>Email Subject</Label>
              <Input
                {...fieldErrorProps("confirmation_subject")}
                className="mt-1 border-gray-300"
                placeholder="Registration Confirmed - {program_name}"
                value={emailSettings.confirmation_subject}
                onChange={(e) => { setEmailSettings({ ...emailSettings, confirmation_subject: e.target.value }); clearFieldIssue("confirmation_subject"); }}
              />
              {renderFieldError("confirmation_subject")}
              <p className="text-xs text-gray-400 mt-1">
                Variables: {"{program_name}"}, {"{player_name}"}, {"{organization_name}"}
              </p>
            </div>

            <div>
              <Label>Email Body</Label>
              <Textarea
                {...fieldErrorProps("confirmation_body")}
                className="mt-1 border-gray-300 font-mono text-sm"
                rows={12}
                value={emailSettings.confirmation_body}
                onChange={(e) => { setEmailSettings({ ...emailSettings, confirmation_body: e.target.value }); clearFieldIssue("confirmation_body"); }}
              />
              {renderFieldError("confirmation_body")}
              <p className="text-xs text-gray-400 mt-1">
                Variables: {"{parent_name}"}, {"{player_name}"}, {"{program_name}"}, {"{start_date}"}, {"{location}"}, {"{organization_name}"}
              </p>
            </div>

          </div>
        );
      case 7:
        return renderReviewStep();

      default:
        return null;
    }
  };

  if (mode === "page") {
    return (
      <div className="athletic-program-wizard min-h-screen bg-[#f3f7f2] text-[#26312f]">
        <header className="athletic-wizard-heading"><h1>{editingProgram ? "Edit program" : "New program"}</h1></header>
        {/* Breadcrumb / Back nav */}
        <div className="flex items-center gap-2 border-b border-[#d8dfd9] bg-[#fbfcf8] px-4 py-3 sm:px-6">
          <button
            onClick={leavePage}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-black transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <span className="text-gray-300">/</span>
          <span className="text-sm text-gray-700 font-medium">
            {editingProgram ? `Edit ${editingProgram.name || "Program"}` : "New Program"}
          </span>
        </div>
        <div className="flex min-h-[calc(100vh-49px)] flex-col lg:flex-row">
          {/* Step Sidebar */}
          <div className="w-full shrink-0 border-b border-[#d8dfd9] bg-[#fbfcf8] p-4 lg:w-64 lg:border-b-0 lg:border-r lg:p-6">
            <h2 className="sr-only">Program creation steps</h2>
            <ProgramStepNavigation steps={STEPS} currentStep={currentStep} onStepChange={goToStep} />
          </div>
          {/* Main Content */}
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
              {renderDraftRecovery()}
              {organizationsError && <div role="alert" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"><span>{organizationsError}</span>{onRetryOrganizations && <Button type="button" variant="outline" size="sm" onClick={onRetryOrganizations} disabled={organizationsLoading}>{organizationsLoading ? "Retrying…" : "Retry"}</Button>}</div>}
              <section className="athletic-wizard-sheet" aria-label="Program configuration">
                {renderStepErrorSummary()}
                {renderStepContent()}
              </section>
            </div>
            {/* Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#d8dfd9] bg-[#fbfcf8] p-4">
              <Button
                variant="outline"
                onClick={currentStep === 1 ? leavePage : prevStep}
                className="border-gray-300"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> {currentStep === 1 ? "Cancel" : "Previous"}
              </Button>
              <div className="order-3 flex w-full items-center justify-center gap-2 sm:order-none sm:w-auto">
                <span className="text-sm text-gray-500">Step {currentStep} of {totalSteps}</span>
                <span className="text-sm text-gray-500"><ProgramDraftSaveStatus serverSavedAt={serverDraftSavedAt} localSavedAt={localDraftSavedAt} saving={serverDraftSaving} /></span>
              </div>
              {currentStep < totalSteps ? (
                <div className="flex items-center gap-2">
                  {editingProgram ? (
                    <Button type="button" variant="outline" onClick={() => handleSubmit(false)} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
                  ) : (
                    <Button type="button" variant="outline" onClick={saveAndExit} disabled={serverDraftSaving}>{serverDraftSaving ? "Saving…" : "Save and exit"}</Button>
                  )}
                  <Button onClick={nextStep} className="bg-[#16877c] text-white hover:bg-[#116d64]">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
                </div>
              ) : (
                editingProgram?.status === "active" ? (
                  <Button onClick={() => handleSubmit(false)} disabled={saving} className="bg-[#16877c] text-white hover:bg-[#116d64]">
                    {saving ? "Saving…" : "Save changes"}
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => handleSubmit(false)} disabled={saving} className="border-gray-300">
                      {saving ? "Saving..." : editingProgram ? "Save changes" : "Save draft"}
                    </Button>
                    <Button onClick={() => getReadiness().ready ? setPublishConfirmationOpen(true) : handleSubmit(true)} disabled={saving} className="bg-[#16877c] text-white hover:bg-[#116d64]">
                      {saving ? "Publishing..." : "Publish program"}
                    </Button>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
        {/* Image Cropper Modal */}
        {cropperOpen && cropperImage && createPortal(
          <ImageCropper
            imageSrc={cropperImage}
            aspectRatio={16 / 9}
            onCropComplete={handleCroppedImageUpload}
            onCancel={() => {
              setCropperOpen(false);
              setCropperImage(null);
              setCropperField(null);
            }}
          />,
          document.body
        )}
        <ProgramPublishConfirmation
          open={publishConfirmationOpen}
          onOpenChange={setPublishConfirmationOpen}
          programDetails={programDetails}
          divisions={divisions}
          saving={saving}
          onConfirm={() => {
            setPublishConfirmationOpen(false);
            handleSubmit(true);
          }}
        />
      </div>
    );
  }

  return (
    <Dialog 
      open={open} 
      modal={!cropperOpen}
      onOpenChange={(isOpen) => {
      // Don't close if cropper is open
      if (cropperOpen) return;
      // Prevent closing if clicking on Google Places autocomplete dropdown
      const pacContainer = document.querySelector('.pac-container');
      if (pacContainer && pacContainer.contains(document.activeElement)) {
        return;
      }
      // Check if the pac-container is visible (user might be selecting)
      if (pacContainer && pacContainer.style.display !== 'none') {
        const pacItems = document.querySelectorAll('.pac-item');
        if (pacItems.length > 0) {
          return; // Don't close if autocomplete is showing
        }
      }
      onOpenChange(isOpen);
    }}>
      <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-hidden bg-white p-0" onPointerDownOutside={(e) => {
        // Prevent closing when clicking on Google Places autocomplete
        if (e.target.closest('.pac-container')) {
          e.preventDefault();
        }
      }} onInteractOutside={(e) => {
        // Prevent closing when interacting with Google Places autocomplete
        if (e.target.closest('.pac-container')) {
          e.preventDefault();
        }
      }}>
        <div className="flex h-[80vh]">
          {/* Step Sidebar */}
          <div className="w-56 bg-gray-50 border-r border-gray-200 p-4">
            <h2 className="font-bold text-black mb-6">
              {editingProgram ? "Edit Program" : "New Program"}
            </h2>
            <ProgramStepNavigation steps={STEPS} currentStep={currentStep} onStepChange={goToStep} />
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col">
            <div className="flex-1 overflow-y-auto p-6">
              {renderDraftRecovery()}
              {organizationsError && <div role="alert" className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{organizationsError}</div>}
              {renderStepErrorSummary()}
              {renderStepContent()}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 flex items-center justify-between bg-white">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
                className="border-gray-300"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Step {currentStep} of {totalSteps}</span>
                <span className="text-sm text-gray-500"><ProgramDraftSaveStatus serverSavedAt={serverDraftSavedAt} localSavedAt={localDraftSavedAt} saving={serverDraftSaving} /></span>
              </div>

              {currentStep < totalSteps ? (
                <Button onClick={nextStep} className="bg-black text-white hover:bg-gray-800">
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => handleSubmit(false)} disabled={saving} className="border-gray-300">
                    {saving ? "Saving..." : "Save draft"}
                  </Button>
                  <Button onClick={() => getReadiness().ready ? setPublishConfirmationOpen(true) : handleSubmit(true)} disabled={saving} className="bg-black text-white hover:bg-gray-800">
                    {saving ? "Publishing..." : "Publish program"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
      
      {/* Image Cropper Modal - rendered via portal to avoid Dialog overlay blocking */}
      {cropperOpen && cropperImage && createPortal(
        <ImageCropper
          imageSrc={cropperImage}
          aspectRatio={16 / 9}
          onCropComplete={handleCroppedImageUpload}
          onCancel={() => {
            setCropperOpen(false);
            setCropperImage(null);
            setCropperField(null);
          }}
        />,
        document.body
      )}
      <ProgramPublishConfirmation
        open={publishConfirmationOpen}
        onOpenChange={setPublishConfirmationOpen}
        programDetails={programDetails}
        divisions={divisions}
        saving={saving}
        onConfirm={() => {
          setPublishConfirmationOpen(false);
          handleSubmit(true);
        }}
      />
    </Dialog>
  );
};

export default ProgramWizard;
